import React, { useState, useEffect, useRef } from "react";
import Map, { NavigationControl, FullscreenControl, Source, Layer, Popup } from "react-map-gl";
import axios from "axios";
import { DOMParser } from "xmldom";
import { Marker } from "react-map-gl";
import { kml } from "@tmcw/togeojson";
import "mapbox-gl/dist/mapbox-gl.css";
import { useDispatch } from "react-redux";
import { getAllprojects } from "../../FeatureRedux/project/getAllprojects";
import JSZip from "jszip";
import { FaMapMarkerAlt, FaTimes, FaChevronRight, FaChevronLeft } from "react-icons/fa";
import { FiCompass } from "react-icons/fi";
import { BiCurrentLocation } from "react-icons/bi";
import MapSidebar from "./MapSidebar";
import { useNavigate } from "react-router-dom";

const Mapbox = () => {
  const [viewState, setViewState] = useState({
    latitude: 20.5937,
    longitude: 78.9629,
    zoom: 0.1,
    pitch: 0,
    bearing: 180,
  });
  const [hoverInfo, setHoverInfo] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState("projects");
  const [pulseMarkers, setPulseMarkers] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);

  const dispatch = useDispatch();
  const [geoJsonData, setGeoJsonData] = useState([]);
  const mapRef = useRef(null);
  const animationRef = useRef(null);
  const [zoom, setZoom] = useState(viewState.zoom);
  const [data, setData] = useState([]);
  const sidebarRef = useRef(null);
  const pulseInterval = useRef(null);
  const navigate = useNavigate();

  // Available sectors for filtering with colors
  const sectors = ["Highways", "Commercial", "Infrastructure", "Construction", "Factory", "Residential", "Education", "Mall", "Railway", "Retail", "Other"];
  
  // Color definitions for all sectors
  const sectorColors = {
    Highways: "bg-blue-500",
    Commercial: "bg-red-500",
    Infrastructure: "bg-yellow-500",
    Construction: "bg-pink-500",
    Factory: "bg-black",
    Residential: "bg-emerald-500",
    Education: "bg-violet-500",
    Mall: "bg-orange-500",
    Railway: "bg-slate-500",
    Retail: "bg-lime-500",
    Other: "bg-gray-500"
  };

  const sectorGradients = {
    Highways: "from-blue-500 to-blue-700",
    Commercial: "from-red-500 to-red-700",
    Infrastructure: "from-yellow-500 to-yellow-700",
    Construction: "from-pink-500 to-pink-700",
    Factory: "from-gray-800 to-black",
    Residential: "from-emerald-500 to-emerald-700",
    Education: "from-violet-500 to-violet-700",
    Mall: "from-orange-500 to-orange-700",
    Railway: "from-slate-500 to-slate-700",
    Retail: "from-lime-500 to-lime-700",
    Other: "from-gray-500 to-gray-700"
  };

  // Map styling colors for sectors
  const mapSectorColors = {
    Highways: '#3b82f6',       // blue
    Commercial: '#ef4444',     // red
    Infrastructure: '#eab308', // yellow
    Construction: '#ec4899',   // pink
    Factory: '#000000',        // black
    Residential: '#10b981',    // emerald
    Education: '#8b5cf6',      // violet
    Mall: '#f97316',           // orange
    Railway: '#64748b',        // slate
    Retail: '#84cc16',         // lime
    Other: '#6b7280'           // gray
  };

  const mapSectorFillColors = {
    Highways: 'rgba(59, 130, 246, 0.2)',
    Commercial: 'rgba(239, 68, 68, 0.2)',
    Infrastructure: 'rgba(234, 179, 8, 0.2)',
    Construction: 'rgba(236, 72, 153, 0.2)',
    Factory: 'rgba(0, 0, 0, 0.2)',
    Residential: 'rgba(16, 185, 129, 0.2)',
    Education: 'rgba(139, 92, 246, 0.2)',
    Mall: 'rgba(249, 115, 22, 0.2)',
    Railway: 'rgba(100, 116, 139, 0.2)',
    Retail: 'rgba(132, 204, 22, 0.2)',
    Other: 'rgba(107, 114, 128, 0.2)'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dispatch(getAllprojects()).unwrap();
        setData(response.projects);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    
    fetchData();
  }, [dispatch]);

  const fetchAndConvertKML = async (url) => {
    try {
      const isKMZ = url.endsWith(".kmz");

      if (isKMZ) {
        const response = await axios.get(url, { responseType: "arraybuffer" });
        const zip = await JSZip.loadAsync(response.data);
        const kmlFile = Object.values(zip.files).find((file) => file.name.endsWith(".kml"));
        if (!kmlFile) {
          console.warn("No .kml file found inside KMZ:", url);
          return null;
        }

        const kmlText = await kmlFile.async("text");
        const parser = new DOMParser();
        const kmlData = parser.parseFromString(kmlText, "text/xml");
        const geoJson = kml(kmlData);
        return geoJson;
      } else {
        const response = await axios.get(url, { responseType: "text" });
        const parser = new DOMParser();
        const kmlData = parser.parseFromString(response.data, "text/xml");
        const geoJson = kml(kmlData);
        return geoJson;
      }
    } catch (error) {
      console.error("Error fetching or converting KML/KMZ:", error, "URL:", url);
      return null;
    }
  };

  useEffect(() => {
    const fetchAllKMLs = async () => {
      if (!Array.isArray(data) || data.length === 0) {
        return;
      }

      let kmlFiles = [];

      for (const project of data) {
        if (Array.isArray(project.kml) && project.kml.length > 0) {
          for (const file of project.kml) {
            try {
              const geoJson = await fetchAndConvertKML(file.url);

              if (geoJson && geoJson.features) {
                geoJson.features.forEach((feature) => {
                  if (!feature.properties) feature.properties = {};
                  feature.properties.project_name = project.project_name;
                  feature.properties.sector = project.sector || "Other";
                  feature.properties.description = project.description;
                  feature.properties._id = project._id;
                });
              }

              if (geoJson && geoJson.features && geoJson.features.length > 0) {
                const allCoords = geoJson.features
                  .map((f) => {
                    if (f.geometry.type === "Point") {
                      return [f.geometry.coordinates];
                    } else if (
                      f.geometry.type === "LineString" ||
                      f.geometry.type === "MultiPoint"
                    ) {
                      return f.geometry.coordinates;
                    } else if (
                      f.geometry.type === "Polygon" ||
                      f.geometry.type === "MultiLineString"
                    ) {
                      return f.geometry.coordinates.flat();
                    } else if (f.geometry.type === "MultiPolygon") {
                      return f.geometry.coordinates.flat(2);
                    }
                    return [];
                  })
                  .flat();

                if (allCoords.length > 0) {
                  const lngs = allCoords.map((c) => c[0]);
                  const lats = allCoords.map((c) => c[1]);

                  const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
                  const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;

                  const center = [avgLng, avgLat];

                  kmlFiles.push({
                    geoJson,
                    projectId: project._id,
                    center,
                    sector: project.sector || "Other",
                    projectData: project
                  });
                }
              }
            } catch (err) {
              console.error("Error processing file:", file.url, err);
            }
          }
        }
      }

      setGeoJsonData(kmlFiles);
    };

    fetchAllKMLs();
  }, [data]);

  useEffect(() => {
    const animateGlobe = () => {
      setIsAnimating(true);
      const startTime = Date.now();
      const duration = 6000;
      const startZoom = 0.1;
      const endZoom = 3;
      const startBearing = 180;
      const endBearing = 0;
      const startPitch = 0;
      const endPitch = 0;
      const targetLng = 78.9629;
      const targetLat = 20.5937;
  
      const animateFrame = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        const easeProgress =
          progress < 0.5
            ? 4 * progress ** 3
            : 1 - ((-2 * progress + 2) ** 3) / 2;
  
        setViewState((prev) => ({
          ...prev,
          zoom: startZoom + (endZoom - startZoom) * easeProgress,
          bearing: startBearing + (endBearing - startBearing) * easeProgress,
          pitch: startPitch + (endPitch - startPitch) * easeProgress,
          longitude: prev.longitude + (targetLng - prev.longitude) * easeProgress,
          latitude: prev.latitude + (targetLat - prev.latitude) * easeProgress,
        }));
  
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animateFrame);
        } else {
          setIsAnimating(false);
        }
      };
  
      animationRef.current = requestAnimationFrame(animateFrame);
    };
  
    const timer = setTimeout(() => {
      animateGlobe();
    }, 1000);
  
    return () => {
      clearTimeout(timer);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      setIsAnimating(false);
    };
  }, []);

  const handleMarkerClick = (center, projectData) => {
    setSelectedProject(projectData);
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [center[0], center[1]],
        zoom: 12,
        speed: 1.2,
        curve: 1.42,
        essential: true,
      });
    }
    if (sidebarRef.current) {
      sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleFilter = (sector) => {
    setActiveFilters(prev =>
      prev.includes(sector)
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
    setSelectedSector(prev => prev === sector ? null : sector);
  };

  const filteredGeoJsonData = activeFilters.length > 0
    ? geoJsonData.filter(item => activeFilters.includes(item.sector))
    : geoJsonData;

  const searchedProjects = searchQuery
    ? filteredGeoJsonData.filter(item =>
        item.projectData.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.projectData.description && item.projectData.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : filteredGeoJsonData;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      document.documentElement.requestFullscreen().catch(e => console.log(e));
    } else {
      document.exitFullscreen().catch(e => console.log(e));
    }
  };

  const resetView = () => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [78.9629, 20.5937],
        zoom: 3,
        bearing: 0,
        pitch: 0,
        speed: 1.2,
        curve: 1.42,
        essential: true,
      });
    }
  };

  const handleFeatureClick = (event) => {
    const features = event.features;
    if (features && features.length > 0) {
      const clickedFeature = features[0];
      if (clickedFeature.properties) {
        const project = geoJsonData.find(
          item => item.projectId === clickedFeature.properties._id
        );
        if (project) {
          handleMarkerClick(project.center, project.projectData);
        }
      }
    }
  };

  const togglePulseMarkers = () => {
    setPulseMarkers(!pulseMarkers);
  };

  useEffect(() => {
    if (pulseMarkers) {
      pulseInterval.current = setInterval(() => {
        // This will trigger re-renders of the markers
      }, 1000);
    } else {
      clearInterval(pulseInterval.current);
    }

    return () => {
      clearInterval(pulseInterval.current);
    };
  }, [pulseMarkers]);

  const flyToLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [position.coords.longitude, position.coords.latitude],
              zoom: 12,
              speed: 1.2,
              curve: 1.42,
              essential: true,
            });
          }
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  };

  return (
    <div className={`relative w-full ${isFullscreen ? 'h-screen' : 'h-full'}`}>
      <div className="relative w-full h-full">
        <Map
          {...viewState}
          ref={mapRef}
          mapboxAccessToken="pk.eyJ1IjoibmlraXRhY2hhdWhhbjEyMyIsImEiOiJjbGwwaWxrdzEwZW02M2pxcjN4eHo1bDR1In0.I4yZh8CAQOz2c63IsCBOpg"
          className="w-full h-full"
          projection="globe"
          mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
          onMove={(evt) => {
            setViewState(evt.viewState);
            setZoom(evt.viewState.zoom);
          }}
          onMouseMove={(event) => {
            const features = event.features;
            const hoveredFeature = features && features[0];

            if (hoveredFeature && hoveredFeature.properties?.project_name) {
              setHoverInfo({
                longitude: event.lngLat.lng,
                latitude: event.lngLat.lat,
                name: hoveredFeature.properties.project_name,
                sector: hoveredFeature.properties.sector || "Other",
                description: hoveredFeature.properties.description
              });
            } else {
              setHoverInfo(null);
            }
          }}
          onClick={handleFeatureClick}
          onMouseLeave={() => setHoverInfo(null)}
          interactiveLayerIds={searchedProjects.map((_, i) => `kml-layer-${i}`)}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <FullscreenControl position="top-right" />

          {searchedProjects.map(({ geoJson, projectId }, index) => (
            <Source key={`${projectId}-${index}`} id={`kml-data-${index}`} type="geojson" data={geoJson}>
              <Layer
                id={`kml-layer-${index}`}
                type="line"
                paint={{
                  "line-color": [
                    'match',
                    ['get', 'sector'],
                    'Highways', mapSectorColors.Highways,
                    'Commercial', mapSectorColors.Commercial,
                    'Infrastructure', mapSectorColors.Infrastructure,
                    'Construction', mapSectorColors.Construction,
                    'Factory', mapSectorColors.Factory,
                    'Residential', mapSectorColors.Residential,
                    'Education', mapSectorColors.Education,
                    'Mall', mapSectorColors.Mall,
                    'Railway', mapSectorColors.Railway,
                    'Retail', mapSectorColors.Retail,
                    /* default */ mapSectorColors.Other
                  ],
                  "line-width": 3,
                  "line-opacity": 0.8
                }}
              />
              <Layer
                id={`kml-fill-${index}`}
                type="fill"
                paint={{
                  "fill-color": [
                    'match',
                    ['get', 'sector'],
                    'Highways', mapSectorFillColors.Highways,
                    'Commercial', mapSectorFillColors.Commercial,
                    'Infrastructure', mapSectorFillColors.Infrastructure,
                    'Construction', mapSectorFillColors.Construction,
                    'Factory', mapSectorFillColors.Factory,
                    'Residential', mapSectorFillColors.Residential,
                    'Education', mapSectorFillColors.Education,
                    'Mall', mapSectorFillColors.Mall,
                    'Railway', mapSectorFillColors.Railway,
                    'Retail', mapSectorFillColors.Retail,
                    /* default */ mapSectorFillColors.Other
                  ],
                  "fill-outline-color": [
                    'match',
                    ['get', 'sector'],
                    'Highways', mapSectorColors.Highways,
                    'Commercial', mapSectorColors.Commercial,
                    'Infrastructure', mapSectorColors.Infrastructure,
                    'Construction', mapSectorColors.Construction,
                    'Factory', mapSectorColors.Factory,
                    'Residential', mapSectorColors.Residential,
                    'Education', mapSectorColors.Education,
                    'Mall', mapSectorColors.Mall,
                    'Railway', mapSectorColors.Railway,
                    'Retail', mapSectorColors.Retail,
                    /* default */ mapSectorColors.Other
                  ]
                }}
              />
            </Source>
          ))}

          {hoverInfo && (
            <Popup
              longitude={hoverInfo.longitude}
              latitude={hoverInfo.latitude}
              closeButton={false}
              closeOnClick={false}
              anchor="top"
              className="z-50"
              maxWidth="300px"
            >
              <div className={`px-4 py-3 rounded-lg shadow-xl border-t-4 transition-all duration-300 ${
                hoverInfo.sector === "Highways" ? "border-blue-500 bg-gray-800" :
                hoverInfo.sector === "Commercial" ? "border-red-500 bg-gray-800" :
                hoverInfo.sector === "Infrastructure" ? "border-yellow-500 bg-gray-800" :
                hoverInfo.sector === "Construction" ? "border-pink-500 bg-gray-800" :
                hoverInfo.sector === "Factory" ? "border-black bg-gray-800" :
                hoverInfo.sector === "Residential" ? "border-emerald-500 bg-gray-800" :
                hoverInfo.sector === "Education" ? "border-violet-500 bg-gray-800" :
                hoverInfo.sector === "Mall" ? "border-orange-500 bg-gray-800" :
                hoverInfo.sector === "Railway" ? "border-slate-500 bg-gray-800" :
                hoverInfo.sector === "Retail" ? "border-lime-500 bg-gray-800" :
                "border-gray-500 bg-gray-800"
              }`}>
                <h4 className="font-bold text-lg mb-1 text-white">{hoverInfo.name}</h4>
                <div className="flex items-center mb-2">
                  <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                    hoverInfo.sector === "Highways" ? "bg-blue-500" :
                    hoverInfo.sector === "Commercial" ? "bg-red-500" :
                    hoverInfo.sector === "Infrastructure" ? "bg-yellow-500" :
                    hoverInfo.sector === "Construction" ? "bg-pink-500" :
                    hoverInfo.sector === "Factory" ? "bg-black" :
                    hoverInfo.sector === "Residential" ? "bg-emerald-500" :
                    hoverInfo.sector === "Education" ? "bg-violet-500" :
                    hoverInfo.sector === "Mall" ? "bg-orange-500" :
                    hoverInfo.sector === "Railway" ? "bg-slate-500" :
                    hoverInfo.sector === "Retail" ? "bg-lime-500" :
                    "bg-gray-500"
                  }`}></span>
                  <span className="text-sm text-gray-300">{hoverInfo.sector}</span>
                </div>
                {hoverInfo.description && (
                  <p className="text-sm text-gray-300 line-clamp-3">{hoverInfo.description}</p>
                )}
              </div>
            </Popup>
          )}

          {zoom <= 7 &&
            searchedProjects.map(({ center, sector, projectData }, index) =>
              center ? (
                <Marker key={index} longitude={center[0]} latitude={center[1]} anchor="bottom">
                  <div 
                    className={`relative cursor-pointer transition-all duration-300 ${
                      pulseMarkers && selectedSector === sector ? 'animate-pulse' : ''
                    }`}
                    onClick={() => handleMarkerClick(center, projectData)}
                  >
                    <div className="group">
                      <div className="relative">
                        <FaMapMarkerAlt
                          size={12}
                          className={`transition-all duration-300 ${
                            sector === "Highways" ? "text-blue-500" :
                            sector === "Commercial" ? "text-red-500" :
                            sector === "Infrastructure" ? "text-yellow-500" :
                            sector === "Construction" ? "text-pink-500" :
                            sector === "Factory" ? "text-black" :
                            sector === "Residential" ? "text-emerald-500" :
                            sector === "Education" ? "text-violet-500" :
                            sector === "Mall" ? "text-orange-500" :
                            sector === "Railway" ? "text-slate-500" :
                            sector === "Retail" ? "text-lime-500" :
                            "text-gray-500"
                          }`}
                        />
                        <div className="absolute inset-0 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300"
                          style={{
                            backgroundColor: 
                              sector === "Highways" ? mapSectorColors.Highways :
                              sector === "Commercial" ? mapSectorColors.Commercial :
                              sector === "Infrastructure" ? mapSectorColors.Infrastructure :
                              sector === "Construction" ? mapSectorColors.Construction :
                              sector === "Factory" ? mapSectorColors.Factory :
                              sector === "Residential" ? mapSectorColors.Residential :
                              sector === "Education" ? mapSectorColors.Education :
                              sector === "Mall" ? mapSectorColors.Mall :
                              sector === "Railway" ? mapSectorColors.Railway :
                              sector === "Retail" ? mapSectorColors.Retail :
                              mapSectorColors.Other
                          }}
                        ></div>
                      </div>
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full animate-ping opacity-75"
                        style={{
                          backgroundColor: 
                            sector === "Highways" ? mapSectorColors.Highways :
                            sector === "Commercial" ? mapSectorColors.Commercial :
                            sector === "Infrastructure" ? mapSectorColors.Infrastructure :
                            sector === "Construction" ? mapSectorColors.Construction :
                            sector === "Factory" ? mapSectorColors.Factory :
                            sector === "Residential" ? mapSectorColors.Residential :
                            sector === "Education" ? mapSectorColors.Education :
                            sector === "Mall" ? mapSectorColors.Mall :
                            sector === "Railway" ? mapSectorColors.Railway :
                            sector === "Retail" ? mapSectorColors.Retail :
                            mapSectorColors.Other
                        }}
                      ></div>
                      <div 
                        className={`absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 text-xs font-medium rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 ${
                          sector === "Highways" ? "bg-blue-500 text-white" :
                          sector === "Commercial" ? "bg-red-500 text-white" :
                          sector === "Infrastructure" ? "bg-yellow-500 text-gray-900" :
                          sector === "Construction" ? "bg-pink-500 text-white" :
                          sector === "Factory" ? "bg-black text-white" :
                          sector === "Residential" ? "bg-emerald-500 text-white" :
                          sector === "Education" ? "bg-violet-500 text-white" :
                          sector === "Mall" ? "bg-orange-500 text-white" :
                          sector === "Railway" ? "bg-slate-500 text-white" :
                          sector === "Retail" ? "bg-lime-500 text-white" :
                          "bg-gray-500 text-white"
                        }`}
                      >
                        {projectData.project_name}
                      </div>
                    </div>
                  </div>
                </Marker>
              ) : null
            )}
        </Map>
      </div>

      {/* Map Controls */}
      <div className="absolute z-10 top-32 right-2 flex flex-col space-y-3">
        <div className="relative group">
          <button
            onClick={resetView}
            className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center border border-gray-700"
          >
            <FiCompass className="text-gray-300 group-hover:text-blue-400 text-lg" />
          </button>
          <span className="absolute -bottom-0.5 -left-10 -translate-x-1/2 bg-gray-700 text-white text-xs rounded py-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap shadow-lg z-10">
            Reset View
          </span>
        </div>
        <button
          onClick={flyToLocation}
          className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group border border-gray-700"
          title="Find My Location"
        >
          <BiCurrentLocation className="text-gray-300 group-hover:text-blue-400 text-lg" />
        </button>
      </div>

      {/* Sidebar Toggle Button */}
      <button
        onClick={toggleSidebar}
        className={`absolute z-10 top-4 left-4 p-3 rounded-full bg-gray-800 shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 border border-gray-700 ${
          sidebarOpen ? 'transform rotate-180' : ''
        }`}
        aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
      >
        {sidebarOpen ? <FaChevronLeft className="text-gray-300" /> : <FaChevronRight className="text-gray-300" />}
      </button>

      {/* Sidebar Component */}
      <MapSidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        toggleFilter={toggleFilter}
        sectors={sectors}
        sectorColors={sectorColors}
        sectorGradients={sectorGradients}
        selectedSector={selectedSector}
        setSelectedSector={setSelectedSector}
        searchedProjects={searchedProjects}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        handleMarkerClick={handleMarkerClick}
        resetView={resetView}
        sidebarRef={sidebarRef}
        isAnimating={isAnimating}
        zoom={zoom}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
      />

      {/* Selected Project Panel */}
      {selectedProject && (
        <div  
          className={`fixed z-10 bottom-7 right-3 w-64 rounded-md transition-all duration-200 transform ${
            selectedProject ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
          }`}
        >
          {/* Glass backdrop */}
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm border border-white/10 rounded-md"></div>
          
          {/* Content */}
          <div className="relative">
            {/* Header */}
            <div className={`px-2.5 py-1.5 flex justify-between items-center border-b border-white/10 ${
              selectedProject.sector === "Highways" ? 'bg-gradient-to-r from-blue-600 to-blue-800' :
              selectedProject.sector === "Commercial" ? 'bg-gradient-to-r from-red-600 to-red-800' :
              selectedProject.sector === "Infrastructure" ? 'bg-gradient-to-r from-yellow-600 to-yellow-800' :
              selectedProject.sector === "Construction" ? 'bg-gradient-to-r from-pink-600 to-pink-800' :
              selectedProject.sector === "Factory" ? 'bg-gradient-to-r from-gray-800 to-black' :
              selectedProject.sector === "Residential" ? 'bg-gradient-to-r from-emerald-600 to-emerald-800' :
              selectedProject.sector === "Education" ? 'bg-gradient-to-r from-violet-600 to-violet-800' :
              selectedProject.sector === "Mall" ? 'bg-gradient-to-r from-orange-600 to-orange-800' :
              selectedProject.sector === "Railway" ? 'bg-gradient-to-r from-slate-600 to-slate-800' :
              selectedProject.sector === "Retail" ? 'bg-gradient-to-r from-lime-600 to-lime-800' :
              'bg-gradient-to-r from-gray-600 to-gray-800'
            }`}>
              <h3 className="text-sm font-medium text-white">Details</h3>
              <button 
                onClick={() => setSelectedProject(null)} 
                className="text-white/80 hover:text-white text-xs p-0.5"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
          
            {/* Body */}
            <div className="p-2 space-y-2">
              <h4 
                className="text-sm font-medium text-white truncate hover:text-blue-300 cursor-pointer"
                onClick={() => navigate('/detail', { state: { project: selectedProject } })}
              >
                {selectedProject.project_name}
              </h4>
              
              <div className="flex items-center text-xs">
                <span className={`w-2 h-2 rounded-full mr-1.5 ${
                  selectedProject.sector === "Highways" ? 'bg-blue-400' :
                  selectedProject.sector === "Commercial" ? 'bg-red-400' :
                  selectedProject.sector === "Infrastructure" ? 'bg-yellow-400' :
                  selectedProject.sector === "Construction" ? 'bg-pink-400' :
                  selectedProject.sector === "Factory" ? 'bg-black' :
                  selectedProject.sector === "Residential" ? 'bg-emerald-400' :
                  selectedProject.sector === "Education" ? 'bg-violet-400' :
                  selectedProject.sector === "Mall" ? 'bg-orange-400' :
                  selectedProject.sector === "Railway" ? 'bg-slate-400' :
                  selectedProject.sector === "Retail" ? 'bg-lime-400' :
                  'bg-gray-400'
                }`}></span>
                <span className="text-white/80">{selectedProject.sector || "Other"}</span>
              </div>
        
              {selectedProject.description && (
                <p className="text-xs text-white/80 line-clamp-2 leading-tight">
                  {selectedProject.description}
                </p>
              )}
        
              <div className="text-[0.65rem] text-white/60 flex justify-between pt-1 border-t border-white/5">
                <span>{new Date(selectedProject.updatedAt).toLocaleDateString()}</span>
                <span>ID: {selectedProject._id.slice(-4)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mapbox;

// import React, { useState, useEffect, useRef } from "react";
// import Map, { NavigationControl, FullscreenControl, Source, Layer, Popup } from "react-map-gl";
// import axios from "axios";
// import { DOMParser } from "xmldom";
// import { Marker } from "react-map-gl";
// import { kml } from "@tmcw/togeojson";
// import "mapbox-gl/dist/mapbox-gl.css";
// import { useDispatch } from "react-redux";
// import { getAllprojects } from "../../FeatureRedux/project/getAllprojects";
// import JSZip from "jszip";
// import { FaMapMarkerAlt, FaTimes, FaChevronRight, FaChevronLeft, } from "react-icons/fa";
// import { FiCompass } from "react-icons/fi";
// import { BiCurrentLocation } from "react-icons/bi";
// import MapSidebar from "./MapSidebar";

// const Mapbox = () => {
//   const [viewState, setViewState] = useState({
//     latitude: 20.5937,
//     longitude: 78.9629,
//     zoom: 0.1,
//     pitch: 0,
//     bearing: 180,
//   });
//   const [hoverInfo, setHoverInfo] = useState(null);
//   const [selectedProject, setSelectedProject] = useState(null);
//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [activeFilters, setActiveFilters] = useState([]);
//   const [isAnimating, setIsAnimating] = useState(false);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [isFullscreen, setIsFullscreen] = useState(false);
//   const [activeTab, setActiveTab] = useState("projects");
//   const [pulseMarkers, setPulseMarkers] = useState(false);
//   const [selectedSector, setSelectedSector] = useState(null);

//   const dispatch = useDispatch();
//   const [geoJsonData, setGeoJsonData] = useState([]);
//   const mapRef = useRef(null);
//   const animationRef = useRef(null);
//   const [zoom, setZoom] = useState(viewState.zoom);
//   const [data, setData] = useState([]);
//   const sidebarRef = useRef(null);
//   const pulseInterval = useRef(null);

//   // Available sectors for filtering with colors
//   const sectors = ["Highways", "Commercial", "Infrastructure", "Construction", "Factory", "Residential", "Education", "Mall", "Railway", "Retail", "Other"];
  
//   // Color definitions for all sectors
//   const sectorColors = {
//     Highways: "bg-blue-500",
//     Commercial: "bg-red-500",
//     Infrastructure: "bg-yellow-500",
//     Construction: "bg-pink-500",
//     Factory: "bg-black",
//     Residential: "bg-emerald-500",
//     Education: "bg-violet-500",
//     Mall: "bg-orange-500",
//     Railway: "bg-slate-500",
//     Retail: "bg-lime-500",
//     Other: "bg-gray-500"
//   };

  

//   const sectorGradients = {
//     Highways: "from-blue-500 to-blue-700",
//     Commercial: "from-red-500 to-red-700",
//     Infrastructure: "from-yellow-500 to-yellow-700",
//     Construction: "from-pink-500 to-pink-700",
//     Factory: "from-gray-800 to-black",
//     Residential: "from-emerald-500 to-emerald-700",
//     Education: "from-violet-500 to-violet-700",
//     Mall: "from-orange-500 to-orange-700",
//     Railway: "from-slate-500 to-slate-700",
//     Retail: "from-lime-500 to-lime-700",
//     Other: "from-gray-500 to-gray-700"
//   };



//   // Map styling colors for sectors
//   const mapSectorColors = {
//     Highways: '#3b82f6',       // blue
//     Commercial: '#ef4444',     // red
//     Infrastructure: '#eab308', // yellow
//     Construction: '#ec4899',   // pink
//     Factory: '#000000',        // black
//     Residential: '#10b981',    // emerald
//     Education: '#8b5cf6',      // violet
//     Mall: '#f97316',           // orange
//     Railway: '#64748b',        // slate
//     Retail: '#84cc16',         // lime
//     Other: '#6b7280'           // gray
//   };

//   const mapSectorFillColors = {
//     Highways: 'rgba(59, 130, 246, 0.2)',
//     Commercial: 'rgba(239, 68, 68, 0.2)',
//     Infrastructure: 'rgba(234, 179, 8, 0.2)',
//     Construction: 'rgba(236, 72, 153, 0.2)',
//     Factory: 'rgba(0, 0, 0, 0.2)',
//     Residential: 'rgba(16, 185, 129, 0.2)',
//     Education: 'rgba(139, 92, 246, 0.2)',
//     Mall: 'rgba(249, 115, 22, 0.2)',
//     Railway: 'rgba(100, 116, 139, 0.2)',
//     Retail: 'rgba(132, 204, 22, 0.2)',
//     Other: 'rgba(107, 114, 128, 0.2)'
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const response = await dispatch(getAllprojects()).unwrap();
//         setData(response.projects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//       }
//     };
    
//     fetchData();
//   }, [dispatch]);

//   const fetchAndConvertKML = async (url) => {
//     try {
//       const isKMZ = url.endsWith(".kmz");

//       if (isKMZ) {
//         const response = await axios.get(url, { responseType: "arraybuffer" });
//         const zip = await JSZip.loadAsync(response.data);
//         const kmlFile = Object.values(zip.files).find((file) => file.name.endsWith(".kml"));
//         if (!kmlFile) {
//           console.warn("No .kml file found inside KMZ:", url);
//           return null;
//         }

//         const kmlText = await kmlFile.async("text");
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(kmlText, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       } else {
//         const response = await axios.get(url, { responseType: "text" });
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(response.data, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       }
//     } catch (error) {
//       console.error("Error fetching or converting KML/KMZ:", error, "URL:", url);
//       return null;
//     }
//   };

//   useEffect(() => {
//     const fetchAllKMLs = async () => {
//       if (!Array.isArray(data) || data.length === 0) {
//         return;
//       }

//       let kmlFiles = [];

//       for (const project of data) {
//         if (Array.isArray(project.kml) && project.kml.length > 0) {
//           for (const file of project.kml) {
//             try {
//               const geoJson = await fetchAndConvertKML(file.url);

//               if (geoJson && geoJson.features) {
//                 geoJson.features.forEach((feature) => {
//                   if (!feature.properties) feature.properties = {};
//                   feature.properties.project_name = project.project_name;
//                   feature.properties.sector = project.sector || "Other";
//                   feature.properties.description = project.description;
//                   feature.properties._id = project._id;
//                 });
//               }

//               if (geoJson && geoJson.features && geoJson.features.length > 0) {
//                 const allCoords = geoJson.features
//                   .map((f) => {
//                     if (f.geometry.type === "Point") {
//                       return [f.geometry.coordinates];
//                     } else if (
//                       f.geometry.type === "LineString" ||
//                       f.geometry.type === "MultiPoint"
//                     ) {
//                       return f.geometry.coordinates;
//                     } else if (
//                       f.geometry.type === "Polygon" ||
//                       f.geometry.type === "MultiLineString"
//                     ) {
//                       return f.geometry.coordinates.flat();
//                     } else if (f.geometry.type === "MultiPolygon") {
//                       return f.geometry.coordinates.flat(2);
//                     }
//                     return [];
//                   })
//                   .flat();

//                 if (allCoords.length > 0) {
//                   const lngs = allCoords.map((c) => c[0]);
//                   const lats = allCoords.map((c) => c[1]);

//                   const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
//                   const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;

//                   const center = [avgLng, avgLat];

//                   kmlFiles.push({
//                     geoJson,
//                     projectId: project._id,
//                     center,
//                     sector: project.sector || "Other",
//                     projectData: project
//                   });
//                 }
//               }
//             } catch (err) {
//               console.error("Error processing file:", file.url, err);
//             }
//           }
//         }
//       }

//       setGeoJsonData(kmlFiles);
//     };

//     fetchAllKMLs();
//   }, [data]);

//   useEffect(() => {
//     const animateGlobe = () => {
//       setIsAnimating(true);
//       const startTime = Date.now();
//       const duration = 6000;
//       const startZoom = 0.1;
//       const endZoom = 3;
//       const startBearing = 180;
//       const endBearing = 0;
//       const startPitch = 0;
//       const endPitch = 0;
//       const targetLng = 78.9629;
//       const targetLat = 20.5937;
  
//       const animateFrame = () => {
//         const now = Date.now();
//         const progress = Math.min((now - startTime) / duration, 1);
//         const easeProgress =
//           progress < 0.5
//             ? 4 * progress ** 3
//             : 1 - ((-2 * progress + 2) ** 3) / 2;
  
//         setViewState((prev) => ({
//           ...prev,
//           zoom: startZoom + (endZoom - startZoom) * easeProgress,
//           bearing: startBearing + (endBearing - startBearing) * easeProgress,
//           pitch: startPitch + (endPitch - startPitch) * easeProgress,
//           longitude: prev.longitude + (targetLng - prev.longitude) * easeProgress,
//           latitude: prev.latitude + (targetLat - prev.latitude) * easeProgress,
//         }));
  
//         if (progress < 1) {
//           animationRef.current = requestAnimationFrame(animateFrame);
//         } else {
//           setIsAnimating(false);
//         }
//       };
  
//       animationRef.current = requestAnimationFrame(animateFrame);
//     };
  
//     const timer = setTimeout(() => {
//       animateGlobe();
//     }, 1000);
  
//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) cancelAnimationFrame(animationRef.current);
//       setIsAnimating(false);
//     };
//   }, []);

//   const handleMarkerClick = (center, projectData) => {
//     setSelectedProject(projectData);
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [center[0], center[1]],
//         zoom: 12,
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//     if (sidebarRef.current) {
//       sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const toggleFilter = (sector) => {
//     setActiveFilters(prev =>
//       prev.includes(sector)
//         ? prev.filter(s => s !== sector)
//         : [...prev, sector]
//     );
//     setSelectedSector(prev => prev === sector ? null : sector);
//   };

//   const filteredGeoJsonData = activeFilters.length > 0
//     ? geoJsonData.filter(item => activeFilters.includes(item.sector))
//     : geoJsonData;

//   const searchedProjects = searchQuery
//     ? filteredGeoJsonData.filter(item =>
//         item.projectData.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
//         (item.projectData.description && item.projectData.description.toLowerCase().includes(searchQuery.toLowerCase()))
//       )
//     : filteredGeoJsonData;

//   const toggleSidebar = () => {
//     setSidebarOpen(!sidebarOpen);
//   };

//   const toggleFullscreen = () => {
//     setIsFullscreen(!isFullscreen);
//     if (!isFullscreen) {
//       document.documentElement.requestFullscreen().catch(e => console.log(e));
//     } else {
//       document.exitFullscreen().catch(e => console.log(e));
//     }
//   };

//   const resetView = () => {
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [78.9629, 20.5937],
//         zoom: 3,
//         bearing: 0,
//         pitch: 0,
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//   };
  


//   const handleFeatureClick = (event) => {
//     const features = event.features;
//     if (features && features.length > 0) {
//       const clickedFeature = features[0];
//       if (clickedFeature.properties) {
//         const project = geoJsonData.find(
//           item => item.projectId === clickedFeature.properties._id
//         );
//         if (project) {
//           handleMarkerClick(project.center, project.projectData);
//         }
//       }
//     }
//   };

//   const togglePulseMarkers = () => {
//     setPulseMarkers(!pulseMarkers);
//   };

//   useEffect(() => {
//     if (pulseMarkers) {
//       pulseInterval.current = setInterval(() => {
//         // This will trigger re-renders of the markers
//       }, 1000);
//     } else {
//       clearInterval(pulseInterval.current);
//     }

//     return () => {
//       clearInterval(pulseInterval.current);
//     };
//   }, [pulseMarkers]);

//   const flyToLocation = () => {
//     if (navigator.geolocation) {
//       navigator.geolocation.getCurrentPosition(
//         (position) => {
//           if (mapRef.current) {
//             mapRef.current.flyTo({
//               center: [position.coords.longitude, position.coords.latitude],
//               zoom: 12,
//               speed: 1.2,
//               curve: 1.42,
//               essential: true,
//             });
//           }
//         },
//         (error) => {
//           console.error("Error getting location:", error);
//         }
//       );
//     }
//   };

//   return (
//     <div className={`relative w-full ${isFullscreen ? 'h-screen' : 'h-full'}`}>
//       <div className="relative w-full h-full">
//         <Map
//           {...viewState}
//           ref={mapRef}
//           mapboxAccessToken="pk.eyJ1IjoibmlraXRhY2hhdWhhbjEyMyIsImEiOiJjbGwwaWxrdzEwZW02M2pxcjN4eHo1bDR1In0.I4yZh8CAQOz2c63IsCBOpg"
//           className="w-full h-full"
//           projection="globe"
//           mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
//           onMove={(evt) => {
//             setViewState(evt.viewState);
//             setZoom(evt.viewState.zoom);
//           }}
//           onMouseMove={(event) => {
//             const features = event.features;
//             const hoveredFeature = features && features[0];

//             if (hoveredFeature && hoveredFeature.properties?.project_name) {
//               setHoverInfo({
//                 longitude: event.lngLat.lng,
//                 latitude: event.lngLat.lat,
//                 name: hoveredFeature.properties.project_name,
//                 sector: hoveredFeature.properties.sector || "Other",
//                 description: hoveredFeature.properties.description
//               });
//             } else {
//               setHoverInfo(null);
//             }
//           }}
//           onClick={handleFeatureClick}
//           onMouseLeave={() => setHoverInfo(null)}
//           interactiveLayerIds={searchedProjects.map((_, i) => `kml-layer-${i}`)}
//         >
//           <NavigationControl position="top-right" showCompass={false} />
//           <FullscreenControl position="top-right" />

//           {searchedProjects.map(({ geoJson, projectId }, index) => (
//             <Source key={`${projectId}-${index}`} id={`kml-data-${index}`} type="geojson" data={geoJson}>
//               <Layer
//                 id={`kml-layer-${index}`}
//                 type="line"
//                 paint={{
//                   "line-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', mapSectorColors.Highways,
//                     'Commercial', mapSectorColors.Commercial,
//                     'Infrastructure', mapSectorColors.Infrastructure,
//                     'Construction', mapSectorColors.Construction,
//                     'Factory', mapSectorColors.Factory,
//                     'Residential', mapSectorColors.Residential,
//                     'Education', mapSectorColors.Education,
//                     'Mall', mapSectorColors.Mall,
//                     'Railway', mapSectorColors.Railway,
//                     'Retail', mapSectorColors.Retail,
//                     /* default */ mapSectorColors.Other
//                   ],
//                   "line-width": 3,
//                   "line-opacity": 0.8
//                 }}
//               />
//               <Layer
//                 id={`kml-fill-${index}`}
//                 type="fill"
//                 paint={{
//                   "fill-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', mapSectorFillColors.Highways,
//                     'Commercial', mapSectorFillColors.Commercial,
//                     'Infrastructure', mapSectorFillColors.Infrastructure,
//                     'Construction', mapSectorFillColors.Construction,
//                     'Factory', mapSectorFillColors.Factory,
//                     'Residential', mapSectorFillColors.Residential,
//                     'Education', mapSectorFillColors.Education,
//                     'Mall', mapSectorFillColors.Mall,
//                     'Railway', mapSectorFillColors.Railway,
//                     'Retail', mapSectorFillColors.Retail,
//                     /* default */ mapSectorFillColors.Other
//                   ],
//                   "fill-outline-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', mapSectorColors.Highways,
//                     'Commercial', mapSectorColors.Commercial,
//                     'Infrastructure', mapSectorColors.Infrastructure,
//                     'Construction', mapSectorColors.Construction,
//                     'Factory', mapSectorColors.Factory,
//                     'Residential', mapSectorColors.Residential,
//                     'Education', mapSectorColors.Education,
//                     'Mall', mapSectorColors.Mall,
//                     'Railway', mapSectorColors.Railway,
//                     'Retail', mapSectorColors.Retail,
//                     /* default */ mapSectorColors.Other
//                   ]
//                 }}
//               />
//             </Source>
//           ))}

//           {hoverInfo && (
//             <Popup
//               longitude={hoverInfo.longitude}
//               latitude={hoverInfo.latitude}
//               closeButton={false}
//               closeOnClick={false}
//               anchor="top"
//               className="z-50"
//               maxWidth="300px"
//             >
//               <div className={`px-4 py-3 rounded-lg shadow-xl border-t-4 transition-all duration-300 ${
//                 hoverInfo.sector === "Highways" ? "border-blue-500 bg-gray-800" :
//                 hoverInfo.sector === "Commercial" ? "border-red-500 bg-gray-800" :
//                 hoverInfo.sector === "Infrastructure" ? "border-yellow-500 bg-gray-800" :
//                 hoverInfo.sector === "Construction" ? "border-pink-500 bg-gray-800" :
//                 hoverInfo.sector === "Factory" ? "border-black bg-gray-800" :
//                 hoverInfo.sector === "Residential" ? "border-emerald-500 bg-gray-800" :
//                 hoverInfo.sector === "Education" ? "border-violet-500 bg-gray-800" :
//                 hoverInfo.sector === "Mall" ? "border-orange-500 bg-gray-800" :
//                 hoverInfo.sector === "Railway" ? "border-slate-500 bg-gray-800" :
//                 hoverInfo.sector === "Retail" ? "border-lime-500 bg-gray-800" :
//                 "border-gray-500 bg-gray-800"
//               }`}>
//                 <h4 className="font-bold text-lg mb-1 text-white">{hoverInfo.name}</h4>
//                 <div className="flex items-center mb-2">
//                   <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
//                     hoverInfo.sector === "Highways" ? "bg-blue-500" :
//                     hoverInfo.sector === "Commercial" ? "bg-red-500" :
//                     hoverInfo.sector === "Infrastructure" ? "bg-yellow-500" :
//                     hoverInfo.sector === "Construction" ? "bg-pink-500" :
//                     hoverInfo.sector === "Factory" ? "bg-black" :
//                     hoverInfo.sector === "Residential" ? "bg-emerald-500" :
//                     hoverInfo.sector === "Education" ? "bg-violet-500" :
//                     hoverInfo.sector === "Mall" ? "bg-orange-500" :
//                     hoverInfo.sector === "Railway" ? "bg-slate-500" :
//                     hoverInfo.sector === "Retail" ? "bg-lime-500" :
//                     "bg-gray-500"
//                   }`}></span>
//                   <span className="text-sm text-gray-300">{hoverInfo.sector}</span>
//                 </div>
//                 {hoverInfo.description && (
//                   <p className="text-sm text-gray-300 line-clamp-3">{hoverInfo.description}</p>
//                 )}
//               </div>
//             </Popup>
//           )}

//           {zoom <= 7 &&
//             searchedProjects.map(({ center, sector, projectData }, index) =>
//               center ? (
//                 <Marker key={index} longitude={center[0]} latitude={center[1]} anchor="bottom">
//                   <div 
//                     className={`relative cursor-pointer transition-all duration-300 ${
//                       pulseMarkers && selectedSector === sector ? 'animate-pulse' : ''
//                     }`}
//                     onClick={() => handleMarkerClick(center, projectData)}
//                   >
//                     <div className="group">
//                       <div className="relative">
//                         <FaMapMarkerAlt
//                           size={12}
//                           className={`transition-all duration-300 ${
//                             sector === "Highways" ? "text-blue-500" :
//                             sector === "Commercial" ? "text-red-500" :
//                             sector === "Infrastructure" ? "text-yellow-500" :
//                             sector === "Construction" ? "text-pink-500" :
//                             sector === "Factory" ? "text-black" :
//                             sector === "Residential" ? "text-emerald-500" :
//                             sector === "Education" ? "text-violet-500" :
//                             sector === "Mall" ? "text-orange-500" :
//                             sector === "Railway" ? "text-slate-500" :
//                             sector === "Retail" ? "text-lime-500" :
//                             "text-gray-500"
//                           }`}
//                         />
//                         <div className="absolute inset-0 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300"
//                           style={{
//                             backgroundColor: 
//                               sector === "Highways" ? mapSectorColors.Highways :
//                               sector === "Commercial" ? mapSectorColors.Commercial :
//                               sector === "Infrastructure" ? mapSectorColors.Infrastructure :
//                               sector === "Construction" ? mapSectorColors.Construction :
//                               sector === "Factory" ? mapSectorColors.Factory :
//                               sector === "Residential" ? mapSectorColors.Residential :
//                               sector === "Education" ? mapSectorColors.Education :
//                               sector === "Mall" ? mapSectorColors.Mall :
//                               sector === "Railway" ? mapSectorColors.Railway :
//                               sector === "Retail" ? mapSectorColors.Retail :
//                               mapSectorColors.Other
//                           }}
//                         ></div>
//                       </div>
//                       <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full animate-ping opacity-75"
//                         style={{
//                           backgroundColor: 
//                             sector === "Highways" ? mapSectorColors.Highways :
//                             sector === "Commercial" ? mapSectorColors.Commercial :
//                             sector === "Infrastructure" ? mapSectorColors.Infrastructure :
//                             sector === "Construction" ? mapSectorColors.Construction :
//                             sector === "Factory" ? mapSectorColors.Factory :
//                             sector === "Residential" ? mapSectorColors.Residential :
//                             sector === "Education" ? mapSectorColors.Education :
//                             sector === "Mall" ? mapSectorColors.Mall :
//                             sector === "Railway" ? mapSectorColors.Railway :
//                             sector === "Retail" ? mapSectorColors.Retail :
//                             mapSectorColors.Other
//                         }}
//                       ></div>
//                       <div 
//                         className={`absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 text-xs font-medium rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 ${
//                           sector === "Highways" ? "bg-blue-500 text-white" :
//                           sector === "Commercial" ? "bg-red-500 text-white" :
//                           sector === "Infrastructure" ? "bg-yellow-500 text-gray-900" :
//                           sector === "Construction" ? "bg-pink-500 text-white" :
//                           sector === "Factory" ? "bg-black text-white" :
//                           sector === "Residential" ? "bg-emerald-500 text-white" :
//                           sector === "Education" ? "bg-violet-500 text-white" :
//                           sector === "Mall" ? "bg-orange-500 text-white" :
//                           sector === "Railway" ? "bg-slate-500 text-white" :
//                           sector === "Retail" ? "bg-lime-500 text-white" :
//                           "bg-gray-500 text-white"
//                         }`}
//                       >
//                         {projectData.project_name}
//                       </div>
//                     </div>
//                   </div>
//                 </Marker>
//               ) : null
//             )}
//         </Map>
//       </div>

//       {/* Map Controls */}
//       <div className="absolute z-10 top-32 right-2 flex flex-col space-y-3">
//         <div className="relative group">
//           <button
//             onClick={resetView}
//             className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center border border-gray-700"
//           >
//             <FiCompass className="text-gray-300 group-hover:text-blue-400 text-lg" />
//           </button>
//           <span className="absolute -bottom-0.5 -left-10 -translate-x-1/2 bg-gray-700 text-white text-xs rounded py-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap shadow-lg z-10">
//             Reset View
//           </span>
//         </div>
//         <button
//           onClick={flyToLocation}
//           className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group border border-gray-700"
//           title="Find My Location"
//         >
//           <BiCurrentLocation className="text-gray-300 group-hover:text-blue-400 text-lg" />
//         </button>
//       </div>

//       {/* Sidebar Toggle Button */}
//       <button
//         onClick={toggleSidebar}
//         className={`absolute z-10 top-4 left-4 p-3 rounded-full bg-gray-800 shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 border border-gray-700 ${
//           sidebarOpen ? 'transform rotate-180' : ''
//         }`}
//         aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
//       >
//         {sidebarOpen ? <FaChevronLeft className="text-gray-300" /> : <FaChevronRight className="text-gray-300" />}
//       </button>

//       {/* Sidebar Component */}
//       <MapSidebar
//         sidebarOpen={sidebarOpen}
//         toggleSidebar={toggleSidebar}
//         activeTab={activeTab}
//         setActiveTab={setActiveTab}
//         searchQuery={searchQuery}
//         setSearchQuery={setSearchQuery}
//         activeFilters={activeFilters}
//         setActiveFilters={setActiveFilters}
//         toggleFilter={toggleFilter}
//         sectors={sectors}
//         sectorColors={sectorColors}
//         sectorGradients={sectorGradients}
//         selectedSector={selectedSector}
//         setSelectedSector={setSelectedSector}
//         searchedProjects={searchedProjects}
//         selectedProject={selectedProject}
//         setSelectedProject={setSelectedProject}
//         handleMarkerClick={handleMarkerClick}
//         resetView={resetView}
//         sidebarRef={sidebarRef}
//         isAnimating={isAnimating}
//         zoom={zoom}
//         isFullscreen={isFullscreen}
//         toggleFullscreen={toggleFullscreen}
//       />

//       {/* Selected Project Panel */}
//       {selectedProject && (
//         <div  
//         className={`fixed z-10 bottom-7 right-3 w-64 rounded-md transition-all duration-200 transform ${
//           selectedProject ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
//         }`}
//       >
//         {/* Glass backdrop */}
//         <div className="absolute inset-0 bg-black/20 backdrop-blur-sm border border-white/10 rounded-md"></div>
        
//         {/* Content */}
//         <div className="relative">
//           {/* Header */}
//           <div className={`px-2.5 py-1.5 flex justify-between items-center border-b border-white/10 ${
//             selectedProject.sector === "Highways" ? 'bg-gradient-to-r from-blue-600 to-blue-800' :
//             selectedProject.sector === "Commercial" ? 'bg-gradient-to-r from-red-600 to-red-800' :
//             // ... other sector gradients (keep all your existing gradient logic)
//             'bg-gradient-to-r from-gray-600 to-gray-800'
//           }`}>
//             <h3 className="text-sm font-medium text-white">Details</h3>
//             <button 
//               onClick={() => setSelectedProject(null)} 
//               className="text-white/80 hover:text-white text-xs p-0.5"
//               aria-label="Close"
//             >
//               <FaTimes />
//             </button>
//           </div>
        
//           {/* Body */}
//           <div className="p-2 space-y-2">
//             <h4 className="text-sm font-medium text-white truncate">
//               {selectedProject.project_name}
//             </h4>
            
//             <div className="flex items-center text-xs">
//               <span className={`w-2 h-2 rounded-full mr-1.5 ${
//                 selectedProject.sector === "Highways" ? 'bg-blue-400' :
//                 selectedProject.sector === "Commercial" ? 'bg-red-400' :
//                 // ... other sector colors
//                 'bg-gray-400'
//               }`}></span>
//               <span className="text-white/80">{selectedProject.sector || "Other"}</span>
//             </div>
      
//             {selectedProject.description && (
//               <p className="text-xs text-white/80 line-clamp-2 leading-tight">
//                 {selectedProject.description}
//               </p>
//             )}
      
//             <div className="text-[0.65rem] text-white/60 flex justify-between pt-1 border-t border-white/5">
//               <span>{new Date(selectedProject.updatedAt).toLocaleDateString()}</span>
//               <span>ID: {selectedProject._id.slice(-4)}</span>
//             </div>
//           </div>
//         </div>
//       </div>
      
//       )}
//     </div>
//   );
// };

// export default Mapbox;

// import React, { useState, useEffect, useRef } from "react";
// import Map, { NavigationControl, FullscreenControl, Source, Layer, Popup } from "react-map-gl";
// import axios from "axios";
// import { DOMParser } from "xmldom";
// import MapboxGL, { Marker } from "react-map-gl";
// import { kml } from "@tmcw/togeojson";
// import "mapbox-gl/dist/mapbox-gl.css";
// import { useDispatch, useSelector } from "react-redux";
// import { getAllprojects } from "../../FeatureRedux/project/getAllprojects";
// import JSZip from "jszip";
// import { FaMapMarkerAlt, FaFilter, FaTimes, FaChevronRight, FaChevronLeft, FaSearch, FaExpand, FaCompress, FaCheck } from "react-icons/fa";
// import { IoMdInformationCircleOutline } from "react-icons/io";
// import { RiEarthFill } from "react-icons/ri";
// import { FiCompass } from "react-icons/fi";
// import { BiCurrentLocation } from "react-icons/bi";
// import MapSidebar from "./MapSidebar";

// const Mapbox = () => {
//   const [viewState, setViewState] = useState({
//     latitude: 20.5937,
//     longitude: 78.9629,
//     zoom: 0.1,
//     pitch: 0,
//     bearing: 180,
//   });
//   const [hoverInfo, setHoverInfo] = useState(null);
//   const [selectedProject, setSelectedProject] = useState(null);
//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [activeFilters, setActiveFilters] = useState([]);
//   const [isAnimating, setIsAnimating] = useState(false);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [isFullscreen, setIsFullscreen] = useState(false);
//   const [activeTab, setActiveTab] = useState("projects");
//   const [pulseMarkers, setPulseMarkers] = useState(false);
//   const [selectedSector, setSelectedSector] = useState(null);

//   const dispatch = useDispatch();
//   const [geoJsonData, setGeoJsonData] = useState([]);
//   const mapRef = useRef(null);
//   const animationRef = useRef(null);
//   const [zoom, setZoom] = useState(viewState.zoom);
//   const [data, setData] = useState([]);
//   const sidebarRef = useRef(null);
//   const pulseInterval = useRef(null);

//   // Available sectors for filtering
//   const sectors = ["Highways", "Commercial", "Infrastructure", "Construction", "Factory", "Residential", "Education", "Mall","Railway","Retail","Other"];
//   const sectorColors = {
//     Highways: "bg-blue-500",
//     Commercial: "bg-red-500",
//     Infrastructure: "bg-yellow-500",
//     Construction: "bg-pink-500",
//     Factory: "bg-black",
//     Other: "bg-green-500"
//   };

//   const sectorBorderColors = {
//     Highways: "border-blue-500",
//     Commercial: "border-red-500",
//     Infrastructure: "border-yellow-500",
//     Construction: "border-pink-500",
//     Factory: "border-black",
//     Other: "border-green-500"
//   };

//   const sectorTextColors = {
//     Highways: "text-blue-500",
//     Commercial: "text-red-500",
//     Infrastructure: "text-yellow-500",
//     Construction: "text-pink-500",
//     Factory: "text-black",
//     Other: "text-green-500"
//   };

//   const sectorGradients = {
//     Highways: "from-blue-500 to-blue-700",
//     Commercial: "from-red-500 to-red-700",
//     Infrastructure: "from-yellow-500 to-yellow-700",
//     Construction: "from-pink-500 to-pink-700",
//     Factory: "from-gray-800 to-black",
//     Other: "from-green-500 to-green-700"
//   };

//   const sectorLightColors = {
//     Highways: "bg-blue-100 text-blue-800",
//     Commercial: "bg-red-100 text-red-800",
//     Infrastructure: "bg-yellow-100 text-yellow-800",
//     Construction: "bg-pink-100 text-pink-800",
//     Factory: "bg-gray-100 text-gray-800",
//     Other: "bg-green-100 text-green-800"
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const response = await dispatch(getAllprojects()).unwrap();
//         setData(response.projects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//       }
//     };
    
//     fetchData();
//   }, [dispatch]);

//   const fetchAndConvertKML = async (url) => {
//     try {
//       const isKMZ = url.endsWith(".kmz");

//       if (isKMZ) {
//         const response = await axios.get(url, { responseType: "arraybuffer" });
//         const zip = await JSZip.loadAsync(response.data);
//         const kmlFile = Object.values(zip.files).find((file) => file.name.endsWith(".kml"));
//         if (!kmlFile) {
//           console.warn("No .kml file found inside KMZ:", url);
//           return null;
//         }

//         const kmlText = await kmlFile.async("text");
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(kmlText, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       } else {
//         const response = await axios.get(url, { responseType: "text" });
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(response.data, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       }
//     } catch (error) {
//       console.error("Error fetching or converting KML/KMZ:", error, "URL:", url);
//       return null;
//     }
//   };

//   useEffect(() => {
//     const fetchAllKMLs = async () => {
//       if (!Array.isArray(data) || data.length === 0) {
//         return;
//       }

//       let kmlFiles = [];

//       for (const project of data) {
//         if (Array.isArray(project.kml) && project.kml.length > 0) {
//           for (const file of project.kml) {
//             try {
//               const geoJson = await fetchAndConvertKML(file.url);

//               if (geoJson && geoJson.features) {
//                 geoJson.features.forEach((feature) => {
//                   if (!feature.properties) feature.properties = {};
//                   feature.properties.project_name = project.project_name;
//                   feature.properties.sector = project.sector || "Other";
//                   feature.properties.description = project.description;
//                   feature.properties._id = project._id;
//                 });
//               }

//               if (geoJson && geoJson.features && geoJson.features.length > 0) {
//                 const allCoords = geoJson.features
//                   .map((f) => {
//                     if (f.geometry.type === "Point") {
//                       return [f.geometry.coordinates];
//                     } else if (
//                       f.geometry.type === "LineString" ||
//                       f.geometry.type === "MultiPoint"
//                     ) {
//                       return f.geometry.coordinates;
//                     } else if (
//                       f.geometry.type === "Polygon" ||
//                       f.geometry.type === "MultiLineString"
//                     ) {
//                       return f.geometry.coordinates.flat();
//                     } else if (f.geometry.type === "MultiPolygon") {
//                       return f.geometry.coordinates.flat(2);
//                     }
//                     return [];
//                   })
//                   .flat();

//                 if (allCoords.length > 0) {
//                   const lngs = allCoords.map((c) => c[0]);
//                   const lats = allCoords.map((c) => c[1]);

//                   const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
//                   const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;

//                   const center = [avgLng, avgLat];

//                   kmlFiles.push({
//                     geoJson,
//                     projectId: project._id,
//                     center,
//                     sector: project.sector || "Other",
//                     projectData: project
//                   });
//                 }
//               }
//             } catch (err) {
//               console.error("Error processing file:", file.url, err);
//             }
//           }
//         }
//       }

//       setGeoJsonData(kmlFiles);
//     };

//     fetchAllKMLs();
//   }, [data]);

//   useEffect(() => {
//     const animateGlobe = () => {
//       setIsAnimating(true);
//       const startTime = Date.now();
//       const duration = 6000;
//       const startZoom = 0.1;
//       const endZoom = 3;
//       const startBearing = 180;
//       const endBearing = 0;
//       const startPitch = 0;
//       const endPitch = 0;
//       const targetLng = 78.9629;
//       const targetLat = 20.5937;
  
//       const animateFrame = () => {
//         const now = Date.now();
//         const progress = Math.min((now - startTime) / duration, 1);
//         const easeProgress =
//           progress < 0.5
//             ? 4 * progress ** 3
//             : 1 - ((-2 * progress + 2) ** 3) / 2;
  
//         setViewState((prev) => ({
//           ...prev,
//           zoom: startZoom + (endZoom - startZoom) * easeProgress,
//           bearing: startBearing + (endBearing - startBearing) * easeProgress,
//           pitch: startPitch + (endPitch - startPitch) * easeProgress,
//           longitude: prev.longitude + (targetLng - prev.longitude) * easeProgress,
//           latitude: prev.latitude + (targetLat - prev.latitude) * easeProgress,
//         }));
  
//         if (progress < 1) {
//           animationRef.current = requestAnimationFrame(animateFrame);
//         } else {
//           setIsAnimating(false);
//         }
//       };
  
//       animationRef.current = requestAnimationFrame(animateFrame);
//     };
  
//     const timer = setTimeout(() => {
//       animateGlobe();
//     }, 1000);
  
//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) cancelAnimationFrame(animationRef.current);
//       setIsAnimating(false);
//     };
//   }, []);

//   const handleMarkerClick = (center, projectData) => {
//     setSelectedProject(projectData);
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [center[0], center[1]],
//         zoom: 12,
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//     if (sidebarRef.current) {
//       sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const toggleFilter = (sector) => {
//     setActiveFilters(prev =>
//       prev.includes(sector)
//         ? prev.filter(s => s !== sector)
//         : [...prev, sector]
//     );
//     setSelectedSector(prev => prev === sector ? null : sector);
//   };

//   const filteredGeoJsonData = activeFilters.length > 0
//     ? geoJsonData.filter(item => activeFilters.includes(item.sector))
//     : geoJsonData;

//   const searchedProjects = searchQuery
//     ? filteredGeoJsonData.filter(item =>
//         item.projectData.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
//         (item.projectData.description && item.projectData.description.toLowerCase().includes(searchQuery.toLowerCase()))
//       )
//     : filteredGeoJsonData;

//   const toggleSidebar = () => {
//     setSidebarOpen(!sidebarOpen);
//   };

//   const toggleFullscreen = () => {
//     setIsFullscreen(!isFullscreen);
//     if (!isFullscreen) {
//       document.documentElement.requestFullscreen().catch(e => console.log(e));
//     } else {
//       document.exitFullscreen().catch(e => console.log(e));
//     }
//   };

//   const resetView = () => {
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [78.9629, 20.5937],
//         zoom: 3,
//         bearing: 0,
//         pitch: 0,
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//   };
  
//   const zoomIn = () => {
//     if (mapRef.current) {
//       mapRef.current.zoomIn();
//     }
//   };

//   const zoomOut = () => {
//     if (mapRef.current) {
//       mapRef.current.zoomOut();
//     }
//   };

//   const handleFeatureClick = (event) => {
//     const features = event.features;
//     if (features && features.length > 0) {
//       const clickedFeature = features[0];
//       if (clickedFeature.properties) {
//         const project = geoJsonData.find(
//           item => item.projectId === clickedFeature.properties._id
//         );
//         if (project) {
//           handleMarkerClick(project.center, project.projectData);
//         }
//       }
//     }
//   };

//   const togglePulseMarkers = () => {
//     setPulseMarkers(!pulseMarkers);
//   };

//   useEffect(() => {
//     if (pulseMarkers) {
//       pulseInterval.current = setInterval(() => {
//         // This will trigger re-renders of the markers
//       }, 1000);
//     } else {
//       clearInterval(pulseInterval.current);
//     }

//     return () => {
//       clearInterval(pulseInterval.current);
//     };
//   }, [pulseMarkers]);

//   const flyToLocation = () => {
//     if (navigator.geolocation) {
//       navigator.geolocation.getCurrentPosition(
//         (position) => {
//           if (mapRef.current) {
//             mapRef.current.flyTo({
//               center: [position.coords.longitude, position.coords.latitude],
//               zoom: 12,
//               speed: 1.2,
//               curve: 1.42,
//               essential: true,
//             });
//           }
//         },
//         (error) => {
//           console.error("Error getting location:", error);
//         }
//       );
//     }
//   };

//   return (
//     <div className={`relative w-full ${isFullscreen ? 'h-screen' : 'h-full'}`}>
//       <div className="relative w-full h-full">
//         <Map
//           {...viewState}
//           ref={mapRef}
//           mapboxAccessToken="pk.eyJ1IjoibmlraXRhY2hhdWhhbjEyMyIsImEiOiJjbGwwaWxrdzEwZW02M2pxcjN4eHo1bDR1In0.I4yZh8CAQOz2c63IsCBOpg"
//           className="w-full h-full"
//           projection="globe"
//           mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
//           onMove={(evt) => {
//             setViewState(evt.viewState);
//             setZoom(evt.viewState.zoom);
//           }}
//           onMouseMove={(event) => {
//             const features = event.features;
//             const hoveredFeature = features && features[0];

//             if (hoveredFeature && hoveredFeature.properties?.project_name) {
//               setHoverInfo({
//                 longitude: event.lngLat.lng,
//                 latitude: event.lngLat.lat,
//                 name: hoveredFeature.properties.project_name,
//                 sector: hoveredFeature.properties.sector || "Other",
//                 description: hoveredFeature.properties.description
//               });
//             } else {
//               setHoverInfo(null);
//             }
//           }}
//           onClick={handleFeatureClick}
//           onMouseLeave={() => setHoverInfo(null)}
//           interactiveLayerIds={searchedProjects.map((_, i) => `kml-layer-${i}`)}
//         >
//           <NavigationControl position="top-right" showCompass={false} />
//           <FullscreenControl position="top-right" />

//           {searchedProjects.map(({ geoJson, projectId }, index) => (
//             <Source key={`${projectId}-${index}`} id={`kml-data-${index}`} type="geojson" data={geoJson}>
//               <Layer
//                 id={`kml-layer-${index}`}
//                 type="line"
//                 paint={{
//                   "line-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', '#3b82f6',
//                     'Commercial', '#ef4444',
//                     'Infrastructure', '#eab308',
//                     'Construction', '#ec4899',
//                     'Factory', '#000000',
//                     /* default */ '#10b981'
//                   ],
//                   "line-width": 3,
//                   "line-opacity": 0.8
//                 }}
//               />
//               <Layer
//                 id={`kml-fill-${index}`}
//                 type="fill"
//                 paint={{
//                   "fill-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', 'rgba(59, 130, 246, 0.2)',
//                     'Commercial', 'rgba(239, 68, 68, 0.2)',
//                     'Infrastructure', 'rgba(234, 179, 8, 0.2)',
//                     'Construction', 'rgba(236, 72, 153, 0.2)',
//                     'Factory', 'rgba(0, 0, 0, 0.2)',
//                     /* default */ 'rgba(16, 185, 129, 0.2)'
//                   ],
//                   "fill-outline-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', '#3b82f6',
//                     'Commercial', '#ef4444',
//                     'Infrastructure', '#eab308',
//                     'Construction', '#ec4899',
//                     'Factory', '#000000',
//                     /* default */ '#10b981'
//                   ]
//                 }}
//               />
//             </Source>
//           ))}

//           {hoverInfo && (
//             <Popup
//               longitude={hoverInfo.longitude}
//               latitude={hoverInfo.latitude}
//               closeButton={false}
//               closeOnClick={false}
//               anchor="top"
//               className="z-50"
//               maxWidth="300px"
//             >
//               <div className={`px-4 py-3 rounded-lg shadow-xl border-t-4 transition-all duration-300 ${
//                 hoverInfo.sector === "Highways" ? "border-blue-500 bg-gray-800" :
//                 hoverInfo.sector === "Commercial" ? "border-red-500 bg-gray-800" :
//                 hoverInfo.sector === "Infrastructure" ? "border-yellow-500 bg-gray-800" :
//                 hoverInfo.sector === "Construction" ? "border-pink-500 bg-gray-800" :
//                 hoverInfo.sector === "Factory" ? "border-black bg-gray-800" :
//                 "border-green-500 bg-gray-800"
//               }`}>
//                 <h4 className="font-bold text-lg mb-1 text-white">{hoverInfo.name}</h4>
//                 <div className="flex items-center mb-2">
//                   <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
//                     hoverInfo.sector === "Highways" ? "bg-blue-500" :
//                     hoverInfo.sector === "Commercial" ? "bg-red-500" :
//                     hoverInfo.sector === "Infrastructure" ? "bg-yellow-500" :
//                     hoverInfo.sector === "Construction" ? "bg-pink-500" :
//                     hoverInfo.sector === "Factory" ? "bg-black" :
//                     "bg-green-500"
//                   }`}></span>
//                   <span className="text-sm text-gray-300">{hoverInfo.sector}</span>
//                 </div>
//                 {hoverInfo.description && (
//                   <p className="text-sm text-gray-300 line-clamp-3">{hoverInfo.description}</p>
//                 )}
//               </div>
//             </Popup>
//           )}

//           {zoom <= 7 &&
//             searchedProjects.map(({ center, sector, projectData }, index) =>
//               center ? (
//                 <Marker key={index} longitude={center[0]} latitude={center[1]} anchor="bottom">
//                   <div 
//                     className={`relative cursor-pointer transition-all duration-300 ${
//                       pulseMarkers && selectedSector === sector ? 'animate-pulse' : ''
//                     }`}
//                     onClick={() => handleMarkerClick(center, projectData)}
//                   >
//                     <div className="group">
//                       <div className="relative">
//                         <FaMapMarkerAlt
//                           size={12}
//                           className={`transition-all duration-300 ${
//                             sector === "Highways" ? "text-blue-500" :
//                             sector === "Commercial" ? "text-red-500" :
//                             sector === "Infrastructure" ? "text-yellow-500" :
//                             sector === "Construction" ? "text-pink-500" :
//                             sector === "Factory" ? "text-black" :
//                             "text-green-500"
//                           }`}
//                         />
//                         <div className="absolute inset-0 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300"
//                           style={{
//                             backgroundColor: 
//                               sector === "Highways" ? "#3b82f6" :
//                               sector === "Commercial" ? "#ef4444" :
//                               sector === "Infrastructure" ? "#eab308" :
//                               sector === "Construction" ? "#ec4899" :
//                               sector === "Factory" ? "#000000" :
//                               "#10b981"
//                           }}
//                         ></div>
//                       </div>
//                       <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full animate-ping opacity-75"
//                         style={{
//                           backgroundColor: 
//                             sector === "Highways" ? "#3b82f6" :
//                             sector === "Commercial" ? "#ef4444" :
//                             sector === "Infrastructure" ? "#eab308" :
//                             sector === "Construction" ? "#ec4899" :
//                             sector === "Factory" ? "#000000" :
//                             "#10b981"
//                         }}
//                       ></div>
//                       <div 
//                         className={`absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 text-xs font-medium rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 ${
//                           sector === "Highways" ? "bg-blue-500 text-white" :
//                           sector === "Commercial" ? "bg-red-500 text-white" :
//                           sector === "Infrastructure" ? "bg-yellow-500 text-gray-900" :
//                           sector === "Construction" ? "bg-pink-500 text-white" :
//                           sector === "Factory" ? "bg-black text-white" :
//                           "bg-green-500 text-white"
//                         }`}
//                       >
//                         {projectData.project_name}
//                       </div>
//                     </div>
//                   </div>
//                 </Marker>
//               ) : null
//             )}
//         </Map>
//       </div>

//       {/* Map Controls */}
//       <div className="absolute z-10 top-32 right-2 flex flex-col space-y-3">
//         <div className="relative group">
//           <button
//             onClick={resetView}
//             className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center border border-gray-700"
//           >
//             <FiCompass className="text-gray-300 group-hover:text-blue-400 text-lg" />
//           </button>
//           <span className="absolute -bottom-0.5 -left-10 -translate-x-1/2 bg-gray-700 text-white text-xs rounded py-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap shadow-lg z-10">
//             Reset View
//           </span>
//         </div>
//         <button
//           onClick={flyToLocation}
//           className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group border border-gray-700"
//           title="Find My Location"
//         >
//           <BiCurrentLocation className="text-gray-300 group-hover:text-blue-400 text-lg" />
//         </button>
//       </div>

//       {/* Sidebar Toggle Button */}
//       <button
//         onClick={toggleSidebar}
//         className={`absolute z-10 top-4 left-4 p-3 rounded-full bg-gray-800 shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 border border-gray-700 ${
//           sidebarOpen ? 'transform rotate-180' : ''
//         }`}
//         aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
//       >
//         {sidebarOpen ? <FaChevronLeft className="text-gray-300" /> : <FaChevronRight className="text-gray-300" />}
//       </button>

//       {/* Sidebar Component */}
//       <MapSidebar
//   sidebarOpen={sidebarOpen}
//   toggleSidebar={toggleSidebar}
//   activeTab={activeTab}
//   setActiveTab={setActiveTab}
//   searchQuery={searchQuery}
//   setSearchQuery={setSearchQuery}
//   activeFilters={activeFilters}
//   setActiveFilters={setActiveFilters} // Add this line
//   toggleFilter={toggleFilter}
//   sectors={sectors}
//   sectorColors={sectorColors}
//   sectorGradients={sectorGradients}
//   selectedSector={selectedSector}
//   setSelectedSector={setSelectedSector} // Add this line
//   searchedProjects={searchedProjects}
//   selectedProject={selectedProject}
//   setSelectedProject={setSelectedProject}
//   handleMarkerClick={handleMarkerClick}
//   resetView={resetView}
//   sidebarRef={sidebarRef}
//   isAnimating={isAnimating}
//   zoom={zoom}
//   isFullscreen={isFullscreen}
//   toggleFullscreen={toggleFullscreen}
// />

//       {/* Selected Project Panel */}
//       {selectedProject && (
//         <div data-aos="fade-up" 
//           className={`absolute z-10 bottom-4 right-4 w-80 bg-gray-800 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 transform ${
//             selectedProject ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
//           } ${sidebarOpen ? 'right-4' : 'right-4'} border border-gray-700`}
//         >
//           <div className={`p-4 bg-gradient-to-r ${
//             selectedProject.sector === "Highways" ? "from-blue-500 to-blue-700" :
//             selectedProject.sector === "Commercial" ? "from-red-500 to-red-700" :
//             selectedProject.sector === "Infrastructure" ? "from-yellow-500 to-yellow-700" :
//             selectedProject.sector === "Construction" ? "from-pink-500 to-pink-700" :
//             selectedProject.sector === "Factory" ? "from-gray-800 to-black" :
//             "from-green-500 to-green-700"
//           } text-white flex justify-between items-center rounded-t-xl border-b border-gray-700`}>
//             <h3 className="font-semibold text-lg tracking-wide">Project Details</h3>
//             <button 
//               onClick={() => setSelectedProject(null)} 
//               className="text-white hover:text-blue-200 transition duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20"
//               aria-label="Close Details"
//             >
//               <FaTimes />
//             </button>
//           </div>
        
//           <div className="p-4 space-y-4">
//             <h4 
//               className="text-lg font-semibold text-white leading-snug hover:text-blue-400 transition-colors cursor-pointer"
//             >
//               {selectedProject.project_name}
//             </h4>
        
//             <div className="flex items-center text-sm">
//               <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
//                 selectedProject.sector === "Highways" ? "bg-blue-500" :
//                 selectedProject.sector === "Commercial" ? "bg-red-500" :
//                 selectedProject.sector === "Infrastructure" ? "bg-yellow-500" :
//                 selectedProject.sector === "Construction" ? "bg-pink-500" :
//                 selectedProject.sector === "Factory" ? "bg-black" :
//                 "bg-green-500"
//               }`}></span>
//               <span className="text-gray-300">{selectedProject.sector || "Other"}</span>
//             </div>
        
//             {selectedProject.description && (
//               <div>
//                 <p className="text-sm text-gray-300 leading-relaxed">{selectedProject.description}</p>
//               </div>
//             )}
        
//             <div className="text-xs text-gray-500 flex justify-between items-center border-t border-gray-700 pt-3">
//               <span>Last updated: {new Date(selectedProject.updatedAt).toLocaleDateString()}</span>
//               <span className="font-mono text-gray-400">ID: {selectedProject._id.slice(-4)}</span>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Mapbox;

// import React, { useState, useEffect, useRef } from "react";
// import Map, { NavigationControl, FullscreenControl, Source, Layer, Popup } from "react-map-gl";
// import axios from "axios";
// import { DOMParser } from "xmldom";
// import MapboxGL, { Marker } from "react-map-gl";
// import { kml } from "@tmcw/togeojson";
// import "mapbox-gl/dist/mapbox-gl.css";
// import { useDispatch, useSelector } from "react-redux";
// import { getAllprojects } from "../../FeatureRedux/project/getAllprojects";
// import JSZip from "jszip";
// import { FaMapMarkerAlt, FaFilter, FaTimes, FaChevronRight, FaChevronLeft, FaSearch, FaExpand, FaCompress, FaCheck } from "react-icons/fa";
// import { IoMdInformationCircleOutline } from "react-icons/io";
// import { RiEarthFill } from "react-icons/ri";
// import { FiCompass } from "react-icons/fi";
// import { BiCurrentLocation } from "react-icons/bi";

// const Mapbox = () => {
//   const [viewState, setViewState] = useState({
//     latitude: 20.5937,
//     longitude: 78.9629,
//     zoom: 0.1,
//     pitch: 0,
//     bearing: 180,
//   });
//   const [hoverInfo, setHoverInfo] = useState(null);
//   const [selectedProject, setSelectedProject] = useState(null);
//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [activeFilters, setActiveFilters] = useState([]);
//   const [isAnimating, setIsAnimating] = useState(false);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [isFullscreen, setIsFullscreen] = useState(false);
//   const [activeTab, setActiveTab] = useState("projects");
//   const [pulseMarkers, setPulseMarkers] = useState(false);
//   const [selectedSector, setSelectedSector] = useState(null);

  
//   const dispatch = useDispatch();
//   const [geoJsonData, setGeoJsonData] = useState([]);
//   const mapRef = useRef(null);
//   const animationRef = useRef(null);
//   const [zoom, setZoom] = useState(viewState.zoom);
//   const [data, setData] = useState([]);
//   const sidebarRef = useRef(null);
//   const pulseInterval = useRef(null);

//   // Available sectors for filtering
//   const sectors = [ "Highways", "Commercial", "Infrastructure", "Construction", "Factory", "Residential", "Industrial", "Agricultural", "Education", "Healthcare", "Transportation", "Tourism", "Government", "Public", "Private", "Mall", "Office", "Hotel", "Hospital", "School", "University", "Park", "Stadium", "Airport", "Railway", "Bus", "Retail", "Restaurant", "Other", ];
  
//   const sectorColors = {
//     Highways: "bg-blue-500",
//     Commercial: "bg-red-500",
//     Infrastructure: "bg-yellow-500",
//     Construction: "bg-pink-500",
//     Factory: "bg-black",
//     Other: "bg-green-500"
//   };

//   const sectorBorderColors = {
//     Highways: "border-blue-500",
//     Commercial: "border-red-500",
//     Infrastructure: "border-yellow-500",
//     Construction: "border-pink-500",
//     Factory: "border-black",
//     Other: "border-green-500"
//   };

//   const sectorTextColors = {
//     Highways: "text-blue-500",
//     Commercial: "text-red-500",
//     Infrastructure: "text-yellow-500",
//     Construction: "text-pink-500",
//     Factory: "text-black",
//     Other: "text-green-500"
//   };

//   const sectorGradients = {
//     Highways: "from-blue-500 to-blue-700",
//     Commercial: "from-red-500 to-red-700",
//     Infrastructure: "from-yellow-500 to-yellow-700",
//     Construction: "from-pink-500 to-pink-700",
//     Factory: "from-gray-800 to-black",
//     Other: "from-green-500 to-green-700"
//   };

//   const sectorLightColors = {
//     Highways: "bg-blue-100 text-blue-800",
//     Commercial: "bg-red-100 text-red-800",
//     Infrastructure: "bg-yellow-100 text-yellow-800",
//     Construction: "bg-pink-100 text-pink-800",
//     Factory: "bg-gray-100 text-gray-800",
//     Other: "bg-green-100 text-green-800"
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const response = await dispatch(getAllprojects()).unwrap();
//         setData(response.projects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//       }
//     };
    
//     fetchData();
//   }, [dispatch]);

//   const fetchAndConvertKML = async (url) => {
//     try {
//       const isKMZ = url.endsWith(".kmz");

//       if (isKMZ) {
//         const response = await axios.get(url, { responseType: "arraybuffer" });
//         const zip = await JSZip.loadAsync(response.data);
//         const kmlFile = Object.values(zip.files).find((file) => file.name.endsWith(".kml"));
//         if (!kmlFile) {
//           console.warn("No .kml file found inside KMZ:", url);
//           return null;
//         }

//         const kmlText = await kmlFile.async("text");
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(kmlText, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       } else {
//         const response = await axios.get(url, { responseType: "text" });
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(response.data, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       }
//     } catch (error) {
//       console.error("Error fetching or converting KML/KMZ:", error, "URL:", url);
//       return null;
//     }
//   };

//   useEffect(() => {
//     const fetchAllKMLs = async () => {
//       if (!Array.isArray(data) || data.length === 0) {
//         return;
//       }

//       let kmlFiles = [];

//       for (const project of data) {
//         if (Array.isArray(project.kml) && project.kml.length > 0) {
//           for (const file of project.kml) {
//             try {
//               const geoJson = await fetchAndConvertKML(file.url);

//               if (geoJson && geoJson.features) {
//                 geoJson.features.forEach((feature) => {
//                   if (!feature.properties) feature.properties = {};
//                   feature.properties.project_name = project.project_name;
//                   feature.properties.sector = project.sector || "Other";
//                   feature.properties.description = project.description;
//                   feature.properties._id = project._id;
//                 });
//               }

//               if (geoJson && geoJson.features && geoJson.features.length > 0) {
//                 const allCoords = geoJson.features
//                   .map((f) => {
//                     if (f.geometry.type === "Point") {
//                       return [f.geometry.coordinates];
//                     } else if (
//                       f.geometry.type === "LineString" ||
//                       f.geometry.type === "MultiPoint"
//                     ) {
//                       return f.geometry.coordinates;
//                     } else if (
//                       f.geometry.type === "Polygon" ||
//                       f.geometry.type === "MultiLineString"
//                     ) {
//                       return f.geometry.coordinates.flat();
//                     } else if (f.geometry.type === "MultiPolygon") {
//                       return f.geometry.coordinates.flat(2);
//                     }
//                     return [];
//                   })
//                   .flat();

//                 if (allCoords.length > 0) {
//                   const lngs = allCoords.map((c) => c[0]);
//                   const lats = allCoords.map((c) => c[1]);

//                   const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
//                   const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;

//                   const center = [avgLng, avgLat];

//                   kmlFiles.push({
//                     geoJson,
//                     projectId: project._id,
//                     center,
//                     sector: project.sector || "Other",
//                     projectData: project
//                   });
//                 }
//               }
//             } catch (err) {
//               console.error("Error processing file:", file.url, err);
//             }
//           }
//         }
//       }

//       setGeoJsonData(kmlFiles);
//     };

//     fetchAllKMLs();
//   }, [data]);
//   useEffect(() => {
//     const animateGlobe = () => {
//       setIsAnimating(true);
//       const startTime = Date.now();
//       const duration = 6000;
//       const startZoom = 0.1;
//       const endZoom = 3;
//       const startBearing = 180;
//       const endBearing = 0;
//       const startPitch = 0;
//       const endPitch = 0;
//       const targetLng = 78.9629;
//       const targetLat = 20.5937;
  
//       const animateFrame = () => {
//         const now = Date.now();
//         const progress = Math.min((now - startTime) / duration, 1);
//         const easeProgress =
//           progress < 0.5
//             ? 4 * progress ** 3
//             : 1 - ((-2 * progress + 2) ** 3) / 2;
  
//         setViewState((prev) => ({
//           ...prev,
//           zoom: startZoom + (endZoom - startZoom) * easeProgress,
//           bearing: startBearing + (endBearing - startBearing) * easeProgress,
//           pitch: startPitch + (endPitch - startPitch) * easeProgress,
//           longitude: prev.longitude + (targetLng - prev.longitude) * easeProgress,
//           latitude: prev.latitude + (targetLat - prev.latitude) * easeProgress,
//         }));
  
//         if (progress < 1) {
//           animationRef.current = requestAnimationFrame(animateFrame);
//         } else {
//           setIsAnimating(false);
//         }
//       };
  
//       animationRef.current = requestAnimationFrame(animateFrame);
//     };
  
//     const timer = setTimeout(() => {
//       animateGlobe();
//     }, 1000);
  
//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) cancelAnimationFrame(animationRef.current);
//       setIsAnimating(false);
//     };
//   }, []);
  


//   const handleMarkerClick = (center, projectData) => {
//     setSelectedProject(projectData);
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [center[0], center[1]],
//         zoom: 12,
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//     if (sidebarRef.current) {
//       sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const toggleFilter = (sector) => {
//     setActiveFilters(prev =>
//       prev.includes(sector)
//         ? prev.filter(s => s !== sector)
//         : [...prev, sector]
//     );
//     setSelectedSector(prev => prev === sector ? null : sector);
//   };

//   const filteredGeoJsonData = activeFilters.length > 0
//     ? geoJsonData.filter(item => activeFilters.includes(item.sector))
//     : geoJsonData;

//   const searchedProjects = searchQuery
//     ? filteredGeoJsonData.filter(item =>
//         item.projectData.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
//         (item.projectData.description && item.projectData.description.toLowerCase().includes(searchQuery.toLowerCase()))
//       )
//     : filteredGeoJsonData;

//   const toggleSidebar = () => {
//     setSidebarOpen(!sidebarOpen);
//   };

//   const toggleFullscreen = () => {
//     setIsFullscreen(!isFullscreen);
//     if (!isFullscreen) {
//       document.documentElement.requestFullscreen().catch(e => console.log(e));
//     } else {
//       document.exitFullscreen().catch(e => console.log(e));
//     }
//   };


//   const resetView = () => {
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [78.9629, 20.5937],
//         zoom: 3,
//         bearing: 0,
//         pitch: 0,  // Changed from 45 to 0 to match animation end state
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//   };
  
  
//   const zoomIn = () => {
//     if (mapRef.current) {
//       mapRef.current.zoomIn();
//     }
//   };

//   const zoomOut = () => {
//     if (mapRef.current) {
//       mapRef.current.zoomOut();
//     }
//   };

//   const handleFeatureClick = (event) => {
//     const features = event.features;
//     if (features && features.length > 0) {
//       const clickedFeature = features[0];
//       if (clickedFeature.properties) {
//         const project = geoJsonData.find(
//           item => item.projectId === clickedFeature.properties._id
//         );
//         if (project) {
//           handleMarkerClick(project.center, project.projectData);
//         }
//       }
//     }
//   };

//   const togglePulseMarkers = () => {
//     setPulseMarkers(!pulseMarkers);
//   };

//   useEffect(() => {
//     if (pulseMarkers) {
//       pulseInterval.current = setInterval(() => {
//         // This will trigger re-renders of the markers
//       }, 1000);
//     } else {
//       clearInterval(pulseInterval.current);
//     }

//     return () => {
//       clearInterval(pulseInterval.current);
//     };
//   }, [pulseMarkers]);

//   const flyToLocation = () => {
//     if (navigator.geolocation) {
//       navigator.geolocation.getCurrentPosition(
//         (position) => {
//           if (mapRef.current) {
//             mapRef.current.flyTo({
//               center: [position.coords.longitude, position.coords.latitude],
//               zoom: 12,
//               speed: 1.2,
//               curve: 1.42,
//               essential: true,
//             });
//           }
//         },
//         (error) => {
//           console.error("Error getting location:", error);
//         }
//       );
//     }
//   };

//   return (
//     <div className={`relative w-full ${isFullscreen ? 'h-screen' : 'h-full'}`}>
//   <div className="relative w-full h-full">
//     <Map
//       {...viewState}
//       ref={mapRef}
//       mapboxAccessToken="pk.eyJ1IjoibmlraXRhY2hhdWhhbjEyMyIsImEiOiJjbGwwaWxrdzEwZW02M2pxcjN4eHo1bDR1In0.I4yZh8CAQOz2c63IsCBOpg"
//       className="w-full h-full"
//       projection="globe"
//       mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
//       onMove={(evt) => {
//         setViewState(evt.viewState);
//         setZoom(evt.viewState.zoom);
//       }}
//       onMouseMove={(event) => {
//         const features = event.features;
//         const hoveredFeature = features && features[0];

//         if (hoveredFeature && hoveredFeature.properties?.project_name) {
//           setHoverInfo({
//             longitude: event.lngLat.lng,
//             latitude: event.lngLat.lat,
//             name: hoveredFeature.properties.project_name,
//             sector: hoveredFeature.properties.sector || "Other",
//             description: hoveredFeature.properties.description
//           });
//         } else {
//           setHoverInfo(null);
//         }
//       }}
//       onClick={handleFeatureClick}
//       onMouseLeave={() => setHoverInfo(null)}
//       interactiveLayerIds={searchedProjects.map((_, i) => `kml-layer-${i}`)}
//     >
//       <NavigationControl position="top-right" showCompass={false} />
//       <FullscreenControl position="top-right" />

//       {searchedProjects.map(({ geoJson, projectId }, index) => (
//         <Source key={`${projectId}-${index}`} id={`kml-data-${index}`} type="geojson" data={geoJson}>
//           <Layer
//             id={`kml-layer-${index}`}
//             type="line"
//             paint={{
//               "line-color": [
//                 'match',
//                 ['get', 'sector'],
//                 'Highways', '#3b82f6',
//                 'Commercial', '#ef4444',
//                 'Infrastructure', '#eab308',
//                 'Construction', '#ec4899',
//                 'Factory', '#000000',
//                 /* default */ '#10b981'
//               ],
//               "line-width": 3,
//               "line-opacity": 0.8
//             }}
//           />
//           <Layer
//             id={`kml-fill-${index}`}
//             type="fill"
//             paint={{
//               "fill-color": [
//                 'match',
//                 ['get', 'sector'],
//                 'Highways', 'rgba(59, 130, 246, 0.2)',
//                 'Commercial', 'rgba(239, 68, 68, 0.2)',
//                 'Infrastructure', 'rgba(234, 179, 8, 0.2)',
//                 'Construction', 'rgba(236, 72, 153, 0.2)',
//                 'Factory', 'rgba(0, 0, 0, 0.2)',
//                 /* default */ 'rgba(16, 185, 129, 0.2)'
//               ],
//               "fill-outline-color": [
//                 'match',
//                 ['get', 'sector'],
//                 'Highways', '#3b82f6',
//                 'Commercial', '#ef4444',
//                 'Infrastructure', '#eab308',
//                 'Construction', '#ec4899',
//                 'Factory', '#000000',
//                 /* default */ '#10b981'
//               ]
//             }}
//           />
//         </Source>
//       ))}

//       {hoverInfo && (
     
//         <Popup
//           longitude={hoverInfo.longitude}
//           latitude={hoverInfo.latitude}
//           closeButton={false}
//           closeOnClick={false}
//           anchor="top"
//           className="z-50"
//           maxWidth="300px"
//         >
//           <div className={`px-4 py-3 rounded-lg shadow-xl border-t-4 transition-all duration-300 ${
//             hoverInfo.sector === "Highways" ? "border-blue-500 bg-gray-800" :
//             hoverInfo.sector === "Commercial" ? "border-red-500 bg-gray-800" :
//             hoverInfo.sector === "Infrastructure" ? "border-yellow-500 bg-gray-800" :
//             hoverInfo.sector === "Construction" ? "border-pink-500 bg-gray-800" :
//             hoverInfo.sector === "Factory" ? "border-black bg-gray-800" :
//             "border-green-500 bg-gray-800"
//           }`}>
//             <h4 className="font-bold text-lg mb-1 text-white">{hoverInfo.name}</h4>
//             <div className="flex items-center mb-2">
//               <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
//                 hoverInfo.sector === "Highways" ? "bg-blue-500" :
//                 hoverInfo.sector === "Commercial" ? "bg-red-500" :
//                 hoverInfo.sector === "Infrastructure" ? "bg-yellow-500" :
//                 hoverInfo.sector === "Construction" ? "bg-pink-500" :
//                 hoverInfo.sector === "Factory" ? "bg-black" :
//                 "bg-green-500"
//               }`}></span>
//               <span className="text-sm text-gray-300">{hoverInfo.sector}</span>
//             </div>
//             {hoverInfo.description && (
//               <p className="text-sm text-gray-300 line-clamp-3">{hoverInfo.description}</p>
//             )}
//           </div>
//         </Popup>
//       )}
//         {zoom <= 7 &&
//   searchedProjects.map(({ center, sector, projectData }, index) =>
//     center ? (
//       <Marker key={index} longitude={center[0]} latitude={center[1]} anchor="bottom">
//         <div 
//           className={`relative cursor-pointer transition-all duration-300 ${
//             pulseMarkers && selectedSector === sector ? 'animate-pulse' : ''
//           }`}
//           onClick={() => handleMarkerClick(center, projectData)}
//         >
//           {/* Marker with hover effect */}
//           <div className="group">
//             <div className="relative">
//               <FaMapMarkerAlt
//                 size={12}
//                 className={`transition-all duration-300 ${
//                   sector === "Highways" ? "text-blue-500" :
//                   sector === "Commercial" ? "text-red-500" :
//                   sector === "Infrastructure" ? "text-yellow-500" :
//                   sector === "Construction" ? "text-pink-500" :
//                   sector === "Factory" ? "text-black" :
//                   "text-green-500"
//                 }`}
//               />
//               <div className="absolute inset-0 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300"
//                 style={{
//                   backgroundColor: 
//                     sector === "Highways" ? "#3b82f6" :
//                     sector === "Commercial" ? "#ef4444" :
//                     sector === "Infrastructure" ? "#eab308" :
//                     sector === "Construction" ? "#ec4899" :
//                     sector === "Factory" ? "#000000" :
//                     "#10b981"
//                 }}
//               ></div>
//             </div>
//             <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full animate-ping opacity-75"
//               style={{
//                 backgroundColor: 
//                   sector === "Highways" ? "#3b82f6" :
//                   sector === "Commercial" ? "#ef4444" :
//                   sector === "Infrastructure" ? "#eab308" :
//                   sector === "Construction" ? "#ec4899" :
//                   sector === "Factory" ? "#000000" :
//                   "#10b981"
//               }}
//             ></div>
//             {/* Tooltip - only shows when directly hovering the marker */}
//             <div 
//               className={`absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 text-xs font-medium rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 ${
//                 sector === "Highways" ? "bg-blue-500 text-white" :
//                 sector === "Commercial" ? "bg-red-500 text-white" :
//                 sector === "Infrastructure" ? "bg-yellow-500 text-gray-900" :
//                 sector === "Construction" ? "bg-pink-500 text-white" :
//                 sector === "Factory" ? "bg-black text-white" :
//                 "bg-green-500 text-white"
//               }`}
            
//             >
              
//               {projectData.project_name}
//             </div>
//           </div>
//         </div>
//       </Marker>
//     ) : null
//   )}
    
//     </Map>
//   </div>

//   {/* Map Controls */}
//   <div className="absolute z-10 top-32 right-2 flex flex-col space-y-3">
//   <div className="relative group">
//   <button
//     onClick={resetView}
//     className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center border border-gray-700"
//   >
//     <FiCompass className="text-gray-300 group-hover:text-blue-400 text-lg" />
//   </button>

//   {/* Tooltip */}
//   <span className="absolute -bottom-0.5 -left-10 -translate-x-1/2 bg-gray-700 text-white text-xs rounded py-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap shadow-lg z-10">
//     Reset View
//   </span>
// </div>

//     {/* <button
//       onClick={resetView}
//       className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group border border-gray-700"
//       title="Reset View"
//     >
//       <FiCompass className="text-gray-300 group-hover:text-blue-400 text-lg" />
//     </button> */}
//     <button
//       onClick={flyToLocation}
//       className="p-3 bg-gray-800 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group border border-gray-700"
//       title="Find My Location"
//     >
//       <BiCurrentLocation className="text-gray-300 group-hover:text-blue-400 text-lg" />
//     </button>
//   </div>

//   {/* Sidebar Toggle Button */}
//   <button
//     onClick={toggleSidebar}
//     className={`absolute z-10 top-4 left-4 p-3 rounded-full bg-gray-800 shadow-lg hover:bg-gray-700 transition-all duration-300 hover:scale-110 active:scale-95 border border-gray-700 ${
//       sidebarOpen ? 'transform rotate-180' : ''
//     }`}
//     aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
//   >
//     {sidebarOpen ? <FaChevronLeft className="text-gray-300" /> : <FaChevronRight className="text-gray-300" />}
//   </button>

//   {/* Enhanced Sidebar */}
//   <div
//     className={`absolute z-10 top-0 left-0 h-full transition-all duration-300 ease-in-out overflow-hidden ${
//       sidebarOpen ? "w-80 md:w-96" : "w-0"
//     }`}
//   >
//     {sidebarOpen && (
//       <div className="h-full flex flex-col bg-gray-900 border-r border-gray-700">
//         {/* Sidebar Header with Gradient */}
//         <div className={`p-4 bg-gradient-to-r ${selectedSector ? sectorGradients[selectedSector] : "from-blue-600 to-blue-800"} text-white flex justify-between items-center border-b border-gray-700`}>
//           <div className="flex items-center">
//             <RiEarthFill className="text-xl mr-2" />
//             <h2 className="text-xl font-bold">Global Projects Map</h2>
//           </div>
//           <button 
//             onClick={toggleSidebar} 
//             className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
//           >
//             <FaTimes />
//           </button>
//         </div>

//         {/* Search & Tabs */}
//         <div className="p-4 border-b border-gray-700">
//           <div className="relative mb-4">
//             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//               <FaSearch className="text-gray-400" />
//             </div>
//             <input
//               type="text"
//               placeholder="Search projects..."
//               className="block w-full pl-10 pr-3 py-2 border border-gray-700 rounded-md leading-5 bg-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 sm:text-sm transition-all duration-200"
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//             />
//           </div>

//           <div className="flex border-b border-gray-700">
//             <button
//               className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                 activeTab === 'projects' 
//                   ? 'text-blue-400'
//                   : 'text-gray-400 hover:text-gray-200'
//               }`}
//               onClick={() => setActiveTab('projects')}
//             >
//               Projects
//               {activeTab === 'projects' && (
//                 <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//               )}
//             </button>
//             <button
//               className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                 activeTab === 'filters' 
//                   ? 'text-blue-400'
//                   : 'text-gray-400 hover:text-gray-200'
//               }`}
//               onClick={() => setActiveTab('filters')}
//             >
//               Filters
//               {activeTab === 'filters' && (
//                 <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//               )}
//             </button>
//           </div>
//         </div>

//         {/* Filters Tab */}
//         {activeTab === 'filters' && (
//           <div className="p-4 border-b border-gray-700 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//             <div className="flex items-center mb-3">
//               <FaFilter className="mr-2 text-blue-400" />
//               <h3 className="font-semibold text-gray-200">Filter by Sector</h3>
//             </div>
            
//             {/* Enhanced Interactive Filters */}
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//               {sectors.map(sector => (
//                 <div key={sector} className="relative">
//                   <input
//                     type="checkbox"
//                     id={`filter-${sector}`}
//                     checked={activeFilters.includes(sector)}
//                     onChange={() => toggleFilter(sector)}
//                     className="absolute opacity-0 h-0 w-0"
//                   />
//                   <label
//                     htmlFor={`filter-${sector}`}
//                     className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
//                       activeFilters.includes(sector)
//                         ? `${sectorColors[sector]} border-transparent`
//                         : 'border-gray-700 bg-gray-800 hover:bg-gray-700'
//                     }`}
//                   >
//                     <div className="flex items-center">
//                       <span className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center transition-all ${
//                         activeFilters.includes(sector) 
//                           ? 'bg-white' 
//                           : 'border border-gray-500'
//                       }`}>
//                         {activeFilters.includes(sector) && (
//                           <FaCheck className="text-xs text-gray-800" />
//                         )}
//                       </span>
//                       <span className={`font-medium ${
//                         activeFilters.includes(sector) ? 'text-white' : 'text-gray-300'
//                       }`}>
//                         {sector}
//                       </span>
//                     </div>
//                     <span className={`text-xs px-2 py-1 rounded-full ${
//                       activeFilters.includes(sector) 
//                         ? 'bg-white bg-opacity-20 text-white' 
//                         : 'bg-gray-700 text-gray-300'
//                     }`}>
//                       {searchedProjects.filter(p => p.projectData.sector === sector).length}
//                     </span>
//                   </label>
//                 </div>
//               ))}
//             </div>

//             {activeFilters.length > 0 && (
//               <button
//                 onClick={() => {
//                   setActiveFilters([]);
//                   setSelectedSector(null);
//                 }}
//                 className="mt-4 w-full py-2 px-4 bg-gray-800 hover:bg-gray-700 text-blue-400 rounded-lg flex items-center justify-center transition-colors duration-200 border border-gray-700"
//               >
//                 <FaTimes className="mr-2" /> Clear all filters
//               </button>
//             )}
//           </div>
//         )}

//         {/* Project List */}
//         <div className="flex-1 overflow-y-auto" ref={sidebarRef} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//           <div className="p-4">
//             {activeTab === 'projects' && (
//               <>
//                 <div className="flex items-center justify-between mb-3">
//                   <h3 className="font-semibold text-gray-300 flex items-center">
//                     <IoMdInformationCircleOutline className="mr-2 text-blue-400" />
//                     {searchQuery 
//                       ? `Search Results (${searchedProjects.length})`
//                       : activeFilters.length > 0
//                         ? `Filtered Projects (${searchedProjects.length})`
//                         : `All Projects (${searchedProjects.length})`}
//                   </h3>
//                   {(searchQuery || activeFilters.length > 0) && (
//                     <button
//                       onClick={() => {
//                         setSearchQuery("");
//                         setActiveFilters([]);
//                         setSelectedSector(null);
//                       }}
//                       className="text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200 flex items-center"
//                     >
//                       <FaTimes className="mr-1" /> Clear
//                     </button>
//                   )}
//                 </div>
//                 {searchedProjects.length === 0 ? (
//                   <div className="text-center py-8 transition-opacity duration-300">
//                     <div className="text-gray-500 mb-2">
//                       <FaSearch className="mx-auto text-3xl" />
//                     </div>
//                     <p className="text-gray-400">
//                       {searchQuery 
//                         ? "No projects match your search"
//                         : activeFilters.length > 0
//                           ? "No projects match the selected filters"
//                           : "No projects available"}
//                     </p>
//                   </div>
//                 ) : (
//                   <ul className="space-y-3">
//                     {searchedProjects.map(({ projectData, center, sector }, index) => (
//                       <li
//                         key={index}
//                         className={`rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md border ${
//                           selectedProject?._id === projectData._id
//                             ? 'border-blue-400 bg-gray-700'
//                             : 'border-gray-700 hover:border-gray-600 bg-gray-800'
//                         }`}
//                       >
//                         <div 
//                           className="p-4"
//                           onClick={() => {
//                             if (selectedProject?._id === projectData._id) {
//                               // If clicking the already selected project, deselect it
//                               setSelectedProject(null);
//                               resetView();
//                             } else {
//                               // Otherwise, select the project and zoom to it
//                               handleMarkerClick(center, projectData);
//                             }
//                           }}
//                         >
//                           <div className="flex items-start">
//                             <div className={`w-4 h-4 rounded-full mt-1 mr-3 flex-shrink-0 ${
//                               sector === "Highways" ? "bg-blue-500" :
//                               sector === "Commercial" ? "bg-red-500" :
//                               sector === "Infrastructure" ? "bg-yellow-500" :
//                               sector === "Construction" ? "bg-pink-500" :
//                               sector === "Factory" ? "bg-black" :
//                               "bg-green-500"
//                             }`}></div>
//                             <div className="flex-1 min-w-0">
//                               <h4 
//                                 className="font-medium text-white truncate hover:text-blue-400 transition-colors"
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   // Navigate to project detail page
//                                   // navigate(`/projects/${projectData._id}`);
//                                 }}
//                               >
//                                 {projectData.project_name}
//                               </h4>
//                               <div className="flex items-center mt-1">
//                                 <span className={`text-xs px-2 py-1 rounded-full ${
//                                   sector === "Highways" ? "bg-blue-900 text-blue-300" :
//                                   sector === "Commercial" ? "bg-red-900 text-red-300" :
//                                   sector === "Infrastructure" ? "bg-yellow-900 text-yellow-300" :
//                                   sector === "Construction" ? "bg-pink-900 text-pink-300" :
//                                   sector === "Factory" ? "bg-gray-700 text-gray-300" :
//                                   "bg-green-900 text-green-300"
//                                 } mr-2`}>
//                                   {sector}
//                                 </span>
//                                 {projectData.description && (
//                                   <p className="text-xs text-gray-400 truncate">
//                                     {projectData.description}
//                                   </p>
//                                 )}
//                               </div>
//                             </div>
//                             <div className="ml-2 text-xs text-gray-500 self-center">
//                               {new Date(projectData.updatedAt).toLocaleDateString()}
//                             </div>
//                           </div>
//                         </div>
//                       </li>
//                     ))}
//                   </ul>
//                 )}
//               </>
//             )}
//           </div>
//         </div>

//         {/* Status Bar */}
//         <div className="p-3 bg-gray-800 text-xs text-gray-400 border-t border-gray-700 flex justify-between items-center">
//           <div className="flex items-center">
//             {isAnimating ? (
//               <span className="flex items-center">
//                 <span className="w-2 h-2 rounded-full bg-blue-500 mr-1 animate-pulse"></span>
//                 Animating...
//               </span>
//             ) : (
//               <span className="flex items-center">
//                 <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
//                 {searchedProjects.length} projects
//               </span>
//             )}
//           </div>
//           <div className="flex items-center space-x-2">
//             <span>Zoom: {zoom.toFixed(2)}</span>
//             <span className="w-px h-4 bg-gray-600"></span>
//             <button 
//               onClick={toggleFullscreen}
//               className="text-gray-400 hover:text-gray-300 transition-colors"
//               title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
//             >
//               {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
//             </button>
//           </div>
//         </div>
//       </div>
//     )}
//   </div>

//   {/* Selected Project Panel */}
//   {selectedProject && (
//     <div data-aos="fade-up" 
//       className={`absolute z-10 bottom-4 right-4 w-80 bg-gray-800 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 transform ${
//         selectedProject ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
//       } ${sidebarOpen ? 'right-4' : 'right-4'} border border-gray-700`}
//     >
//       {/* Header */}
//       <div className={`p-4 bg-gradient-to-r ${
//         selectedProject.sector === "Highways" ? "from-blue-500 to-blue-700" :
//         selectedProject.sector === "Commercial" ? "from-red-500 to-red-700" :
//         selectedProject.sector === "Infrastructure" ? "from-yellow-500 to-yellow-700" :
//         selectedProject.sector === "Construction" ? "from-pink-500 to-pink-700" :
//         selectedProject.sector === "Factory" ? "from-gray-800 to-black" :
//         "from-green-500 to-green-700"
//       } text-white flex justify-between items-center rounded-t-xl border-b border-gray-700`}>
//         <h3 className="font-semibold text-lg tracking-wide">Project Details</h3>
//         <button 
//           onClick={() => setSelectedProject(null)} 
//           className="text-white hover:text-blue-200 transition duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20"
//           aria-label="Close Details"
//         >
//           <FaTimes />
//         </button>
//       </div>
    
//       {/* Content */}
//       <div className="p-4 space-y-4">
//         {/* Project Name */}
//         <h4 
//           className="text-lg font-semibold text-white leading-snug hover:text-blue-400 transition-colors cursor-pointer"
//           // onClick={() => navigate(`/projects/${selectedProject._id}`)}
//         >
//           {selectedProject.project_name}
          
//         </h4>
    
//         {/* Sector */}
//         <div className="flex items-center text-sm">
//           <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
//             selectedProject.sector === "Highways" ? "bg-blue-500" :
//             selectedProject.sector === "Commercial" ? "bg-red-500" :
//             selectedProject.sector === "Infrastructure" ? "bg-yellow-500" :
//             selectedProject.sector === "Construction" ? "bg-pink-500" :
//             selectedProject.sector === "Factory" ? "bg-black" :
//             "bg-green-500"
//           }`}></span>
//           <span className="text-gray-300">{selectedProject.sector || "Other"}</span>
//         </div>
    
//         {/* Description */}
//         {selectedProject.description && (
//           <div>
//             <p className="text-sm text-gray-300 leading-relaxed">{selectedProject.description}</p>
//           </div>
//         )}
    
//         {/* Footer Info */}
//         <div className="text-xs text-gray-500 flex justify-between items-center border-t border-gray-700 pt-3">
//           <span>Last updated: {new Date(selectedProject.updatedAt).toLocaleDateString()}</span>
//           <span className="font-mono text-gray-400">ID: {selectedProject._id.slice(-4)}</span>
//         </div>
//       </div>
//     </div>
//   )}
// </div>
//   );
// };

// export default Mapbox;


// import React, { useState, useEffect, useRef } from "react";
// import Map, { NavigationControl, FullscreenControl, Source, Layer, Popup } from "react-map-gl";
// import axios from "axios";
// import { DOMParser } from "xmldom";
// import MapboxGL, { Marker } from "react-map-gl";
// import { kml } from "@tmcw/togeojson";
// import "mapbox-gl/dist/mapbox-gl.css";
// import { useDispatch, useSelector } from "react-redux";
// import { getAllprojects } from "../../FeatureRedux/project/getAllprojects";
// import JSZip from "jszip";
// import { 
//   FaMapMarkerAlt, 
//   FaFilter, 
//   FaInfoCircle, 
//   FaTimes, 
//   FaChevronRight, 
//   FaChevronLeft,
//   FaSearch,
//   FaExpand,
//   FaCompress,
//   FaGlobeAmericas,
//   FaExternalLinkAlt
// } from "react-icons/fa";
// import { IoMdInformationCircleOutline } from "react-icons/io";
// import { RiEarthFill, RiPulseFill } from "react-icons/ri";
// import { FiZoomIn, FiZoomOut, FiCompass } from "react-icons/fi";
// import { BiCurrentLocation } from "react-icons/bi";

// const Mapbox = () => {
//   const [viewState, setViewState] = useState({
//     latitude: 20.5937,
//     longitude: 78.9629,
//     zoom: 0.1,
//     pitch: 60,
//     bearing: 180,
//   });
//   const [hoverInfo, setHoverInfo] = useState(null);
//   const [selectedProject, setSelectedProject] = useState(null);
//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [activeFilters, setActiveFilters] = useState([]);
//   const [isAnimating, setIsAnimating] = useState(false);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [isFullscreen, setIsFullscreen] = useState(false);
//   const [activeTab, setActiveTab] = useState("projects");
//   const [pulseMarkers, setPulseMarkers] = useState(false);
//   const [selectedSector, setSelectedSector] = useState(null);
  
//   const dispatch = useDispatch();
//   const [geoJsonData, setGeoJsonData] = useState([]);
//   const mapRef = useRef(null);
//   const animationRef = useRef(null);
//   const [zoom, setZoom] = useState(viewState.zoom);
//   const [data, setData] = useState([]);
//   const sidebarRef = useRef(null);
//   const pulseInterval = useRef(null);

//   // Available sectors for filtering
//   const sectors = [
//     "Highways",
//     "Commercial",
//     "Infrastructure",
//     "Construction",
//     "Factory",
//     "Other"
//   ];

//   const sectorColors = {
//     Highways: "bg-blue-500",
//     Commercial: "bg-red-500",
//     Infrastructure: "bg-yellow-500",
//     Construction: "bg-pink-500",
//     Factory: "bg-black",
//     Other: "bg-green-500"
//   };

//   const sectorBorderColors = {
//     Highways: "border-blue-500",
//     Commercial: "border-red-500",
//     Infrastructure: "border-yellow-500",
//     Construction: "border-pink-500",
//     Factory: "border-black",
//     Other: "border-green-500"
//   };

//   const sectorTextColors = {
//     Highways: "text-blue-500",
//     Commercial: "text-red-500",
//     Infrastructure: "text-yellow-500",
//     Construction: "text-pink-500",
//     Factory: "text-black",
//     Other: "text-green-500"
//   };

//   const sectorGradients = {
//     Highways: "from-blue-500 to-blue-700",
//     Commercial: "from-red-500 to-red-700",
//     Infrastructure: "from-yellow-500 to-yellow-700",
//     Construction: "from-pink-500 to-pink-700",
//     Factory: "from-gray-800 to-black",
//     Other: "from-green-500 to-green-700"
//   };

//   const sectorLightColors = {
//     Highways: "bg-blue-100 text-blue-800",
//     Commercial: "bg-red-100 text-red-800",
//     Infrastructure: "bg-yellow-100 text-yellow-800",
//     Construction: "bg-pink-100 text-pink-800",
//     Factory: "bg-gray-100 text-gray-800",
//     Other: "bg-green-100 text-green-800"
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const response = await dispatch(getAllprojects()).unwrap();
//         setData(response.projects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//       }
//     };
    
//     fetchData();
//   }, [dispatch]);

//   const fetchAndConvertKML = async (url) => {
//     try {
//       const isKMZ = url.endsWith(".kmz");

//       if (isKMZ) {
//         const response = await axios.get(url, { responseType: "arraybuffer" });
//         const zip = await JSZip.loadAsync(response.data);
//         const kmlFile = Object.values(zip.files).find((file) => file.name.endsWith(".kml"));
//         if (!kmlFile) {
//           console.warn("No .kml file found inside KMZ:", url);
//           return null;
//         }

//         const kmlText = await kmlFile.async("text");
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(kmlText, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       } else {
//         const response = await axios.get(url, { responseType: "text" });
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(response.data, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       }
//     } catch (error) {
//       console.error("Error fetching or converting KML/KMZ:", error, "URL:", url);
//       return null;
//     }
//   };

//   useEffect(() => {
//     const fetchAllKMLs = async () => {
//       if (!Array.isArray(data) || data.length === 0) {
//         return;
//       }

//       let kmlFiles = [];

//       for (const project of data) {
//         if (Array.isArray(project.kml) && project.kml.length > 0) {
//           for (const file of project.kml) {
//             try {
//               const geoJson = await fetchAndConvertKML(file.url);

//               if (geoJson && geoJson.features) {
//                 geoJson.features.forEach((feature) => {
//                   if (!feature.properties) feature.properties = {};
//                   feature.properties.project_name = project.project_name;
//                   feature.properties.sector = project.sector || "Other";
//                   feature.properties.description = project.description;
//                   feature.properties._id = project._id;
//                 });
//               }

//               if (geoJson && geoJson.features && geoJson.features.length > 0) {
//                 const allCoords = geoJson.features
//                   .map((f) => {
//                     if (f.geometry.type === "Point") {
//                       return [f.geometry.coordinates];
//                     } else if (
//                       f.geometry.type === "LineString" ||
//                       f.geometry.type === "MultiPoint"
//                     ) {
//                       return f.geometry.coordinates;
//                     } else if (
//                       f.geometry.type === "Polygon" ||
//                       f.geometry.type === "MultiLineString"
//                     ) {
//                       return f.geometry.coordinates.flat();
//                     } else if (f.geometry.type === "MultiPolygon") {
//                       return f.geometry.coordinates.flat(2);
//                     }
//                     return [];
//                   })
//                   .flat();

//                 if (allCoords.length > 0) {
//                   const lngs = allCoords.map((c) => c[0]);
//                   const lats = allCoords.map((c) => c[1]);

//                   const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
//                   const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;

//                   const center = [avgLng, avgLat];

//                   kmlFiles.push({
//                     geoJson,
//                     projectId: project._id,
//                     center,
//                     sector: project.sector || "Other",
//                     projectData: project
//                   });
//                 }
//               }
//             } catch (err) {
//               console.error("Error processing file:", file.url, err);
//             }
//           }
//         }
//       }

//       setGeoJsonData(kmlFiles);
//     };

//     fetchAllKMLs();
//   }, [data]);
//   useEffect(() => {
//     const animateGlobe = () => {
//       setIsAnimating(true);
//       const startTime = Date.now();
//       const duration = 6000;
//       const startZoom = 0.1; // 🔥 Changed from 0.5 to 0.1 to start farther
//       const endZoom = 3;
//       const startBearing = 180;
//       const endBearing = 0; // Final orientation
//       const startPitch = 60;
//       const endPitch = 0;
//       const targetLng = 78.9629; // India center
//       const targetLat = 20.5937;
  
//       const animateFrame = () => {
//         const now = Date.now();
//         const progress = Math.min((now - startTime) / duration, 1);
//         const easeProgress =
//           progress < 0.5
//             ? 4 * progress ** 3
//             : 1 - ((-2 * progress + 2) ** 3) / 2;
  
//         setViewState((prev) => ({
//           ...prev,
//           zoom: startZoom + (endZoom - startZoom) * easeProgress,
//           bearing: startBearing + (endBearing - startBearing) * easeProgress,
//           pitch: startPitch + (endPitch - startPitch) * easeProgress,
//           longitude: prev.longitude + (targetLng - prev.longitude) * easeProgress,
//           latitude: prev.latitude + (targetLat - prev.latitude) * easeProgress,
//         }));
  
//         if (progress < 1) {
//           animationRef.current = requestAnimationFrame(animateFrame);
//         } else {
//           setIsAnimating(false);
//         }
//       };
  
//       animationRef.current = requestAnimationFrame(animateFrame);
//     };
  
//     const timer = setTimeout(() => {
//       animateGlobe();
//     }, 1000);
  
//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) cancelAnimationFrame(animationRef.current);
//       setIsAnimating(false);
//     };
//   }, []);
  
  
//   // useEffect(() => {
//   //   const animateGlobe = () => {
//   //     setIsAnimating(true);
//   //     const startTime = Date.now();
//   //     const duration = 4000;
//   //     const startZoom = 0.5;
//   //     const endZoom = 3;
//   //     const startBearing = 180;
//   //     const endBearing = 0;
//   //     const startPitch = 0;
//   //     const endPitch = 45;

//   //     const animateFrame = () => {
//   //       const now = Date.now();
//   //       const progress = Math.min((now - startTime) / duration, 1);
//   //       const easeProgress = progress < 0.5 ? 4 * progress ** 3 : 1 - ((-2 * progress + 2) ** 3) / 2;

//   //       setViewState((prev) => ({
//   //         ...prev,
//   //         zoom: startZoom + (endZoom - startZoom) * easeProgress,
//   //         bearing: startBearing + (endBearing - startBearing) * easeProgress,
//   //         pitch: startPitch + (endPitch - startPitch) * easeProgress
//   //       }));

//   //       if (progress < 1) {
//   //         animationRef.current = requestAnimationFrame(animateFrame);
//   //       } else {
//   //         setIsAnimating(false);
//   //       }
//   //     };

//   //     animationRef.current = requestAnimationFrame(animateFrame);
//   //   };

//   //   const timer = setTimeout(() => {
//   //     animateGlobe();
//   //   }, 1000);
    
//   //   return () => {
//   //     clearTimeout(timer);
//   //     if (animationRef.current) cancelAnimationFrame(animationRef.current);
//   //     setIsAnimating(false);
//   //   };
//   // }, []);

//   const handleMarkerClick = (center, projectData) => {
//     setSelectedProject(projectData);
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [center[0], center[1]],
//         zoom: 12,
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//     if (sidebarRef.current) {
//       sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const toggleFilter = (sector) => {
//     setActiveFilters(prev =>
//       prev.includes(sector)
//         ? prev.filter(s => s !== sector)
//         : [...prev, sector]
//     );
//     setSelectedSector(prev => prev === sector ? null : sector);
//   };

//   const filteredGeoJsonData = activeFilters.length > 0
//     ? geoJsonData.filter(item => activeFilters.includes(item.sector))
//     : geoJsonData;

//   const searchedProjects = searchQuery
//     ? filteredGeoJsonData.filter(item =>
//         item.projectData.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
//         (item.projectData.description && item.projectData.description.toLowerCase().includes(searchQuery.toLowerCase()))
//       )
//     : filteredGeoJsonData;

//   const toggleSidebar = () => {
//     setSidebarOpen(!sidebarOpen);
//   };

//   const toggleFullscreen = () => {
//     setIsFullscreen(!isFullscreen);
//     if (!isFullscreen) {
//       document.documentElement.requestFullscreen().catch(e => console.log(e));
//     } else {
//       document.exitFullscreen().catch(e => console.log(e));
//     }
//   };

//   const resetView = () => {
//     if (mapRef.current) {
//       mapRef.current.flyTo({
//         center: [78.9629, 20.5937],
//         zoom: 3,
//         bearing: 0,
//         pitch: 45,
//         speed: 1.2,
//         curve: 1.42,
//         essential: true,
//       });
//     }
//   };

//   const zoomIn = () => {
//     if (mapRef.current) {
//       mapRef.current.zoomIn();
//     }
//   };

//   const zoomOut = () => {
//     if (mapRef.current) {
//       mapRef.current.zoomOut();
//     }
//   };

//   const handleFeatureClick = (event) => {
//     const features = event.features;
//     if (features && features.length > 0) {
//       const clickedFeature = features[0];
//       if (clickedFeature.properties) {
//         const project = geoJsonData.find(
//           item => item.projectId === clickedFeature.properties._id
//         );
//         if (project) {
//           handleMarkerClick(project.center, project.projectData);
//         }
//       }
//     }
//   };

//   const togglePulseMarkers = () => {
//     setPulseMarkers(!pulseMarkers);
//   };

//   useEffect(() => {
//     if (pulseMarkers) {
//       pulseInterval.current = setInterval(() => {
//         // This will trigger re-renders of the markers
//       }, 1000);
//     } else {
//       clearInterval(pulseInterval.current);
//     }

//     return () => {
//       clearInterval(pulseInterval.current);
//     };
//   }, [pulseMarkers]);

//   const flyToLocation = () => {
//     if (navigator.geolocation) {
//       navigator.geolocation.getCurrentPosition(
//         (position) => {
//           if (mapRef.current) {
//             mapRef.current.flyTo({
//               center: [position.coords.longitude, position.coords.latitude],
//               zoom: 12,
//               speed: 1.2,
//               curve: 1.42,
//               essential: true,
//             });
//           }
//         },
//         (error) => {
//           console.error("Error getting location:", error);
//         }
//       );
//     }
//   };

//   return (
//     <div className={`relative w-full ${isFullscreen ? 'h-screen' : 'h-full'}`}>
//       <div className="relative w-full h-full">
//         <Map
//           {...viewState}
//           ref={mapRef}
//           mapboxAccessToken="pk.eyJ1IjoibmlraXRhY2hhdWhhbjEyMyIsImEiOiJjbGwwaWxrdzEwZW02M2pxcjN4eHo1bDR1In0.I4yZh8CAQOz2c63IsCBOpg"
//           className="w-full h-full"
//           projection="globe"
//           mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
//           onMove={(evt) => {
//             setViewState(evt.viewState);
//             setZoom(evt.viewState.zoom);
//           }}
//           onMouseMove={(event) => {
//             const features = event.features;
//             const hoveredFeature = features && features[0];

//             if (hoveredFeature && hoveredFeature.properties?.project_name) {
//               setHoverInfo({
//                 longitude: event.lngLat.lng,
//                 latitude: event.lngLat.lat,
//                 name: hoveredFeature.properties.project_name,
//                 sector: hoveredFeature.properties.sector || "Other",
//                 description: hoveredFeature.properties.description
//               });
//             } else {
//               setHoverInfo(null);
//             }
//           }}
//           onClick={handleFeatureClick}
//           onMouseLeave={() => setHoverInfo(null)}
//           interactiveLayerIds={searchedProjects.map((_, i) => `kml-layer-${i}`)}
//         >
//           <NavigationControl position="top-right" showCompass={false} />
//           <FullscreenControl position="top-right" />

//           {searchedProjects.map(({ geoJson, projectId }, index) => (
//             <Source key={`${projectId}-${index}`} id={`kml-data-${index}`} type="geojson" data={geoJson}>
//               <Layer
//                 id={`kml-layer-${index}`}
//                 type="line"
//                 paint={{
//                   "line-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', '#3b82f6',
//                     'Commercial', '#ef4444',
//                     'Infrastructure', '#eab308',
//                     'Construction', '#ec4899',
//                     'Factory', '#000000',
//                     /* default */ '#10b981'
//                   ],
//                   "line-width": 3,
//                   "line-opacity": 0.8
//                 }}
//               />
//               <Layer
//                 id={`kml-fill-${index}`}
//                 type="fill"
//                 paint={{
//                   "fill-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', 'rgba(59, 130, 246, 0.2)',
//                     'Commercial', 'rgba(239, 68, 68, 0.2)',
//                     'Infrastructure', 'rgba(234, 179, 8, 0.2)',
//                     'Construction', 'rgba(236, 72, 153, 0.2)',
//                     'Factory', 'rgba(0, 0, 0, 0.2)',
//                     /* default */ 'rgba(16, 185, 129, 0.2)'
//                   ],
//                   "fill-outline-color": [
//                     'match',
//                     ['get', 'sector'],
//                     'Highways', '#3b82f6',
//                     'Commercial', '#ef4444',
//                     'Infrastructure', '#eab308',
//                     'Construction', '#ec4899',
//                     'Factory', '#000000',
//                     /* default */ '#10b981'
//                   ]
//                 }}
//               />
//             </Source>
//           ))}

//           {hoverInfo && (
//             <Popup
//               longitude={hoverInfo.longitude}
//               latitude={hoverInfo.latitude}
//               closeButton={false}
//               closeOnClick={false}
//               anchor="top"
//               className="z-50"
//               maxWidth="300px"
//             >
//               <div className={`px-4 py-3 rounded-lg shadow-xl border-t-4 transition-all duration-300 ${
//                 hoverInfo.sector === "Highways" ? "border-blue-500 bg-blue-50" :
//                 hoverInfo.sector === "Commercial" ? "border-red-500 bg-red-50" :
//                 hoverInfo.sector === "Infrastructure" ? "border-yellow-500 bg-yellow-50" :
//                 hoverInfo.sector === "Construction" ? "border-pink-500 bg-pink-50" :
//                 hoverInfo.sector === "Factory" ? "border-black bg-gray-100" :
//                 "border-green-500 bg-green-50"
//               }`}>
//                 <h4 className="font-bold text-lg mb-1">{hoverInfo.name}</h4>
//                 <div className="flex items-center mb-2">
//                   <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
//                     hoverInfo.sector === "Highways" ? "bg-blue-500" :
//                     hoverInfo.sector === "Commercial" ? "bg-red-500" :
//                     hoverInfo.sector === "Infrastructure" ? "bg-yellow-500" :
//                     hoverInfo.sector === "Construction" ? "bg-pink-500" :
//                     hoverInfo.sector === "Factory" ? "bg-black" :
//                     "bg-green-500"
//                   }`}></span>
//                   <span className="text-sm text-gray-600">{hoverInfo.sector}</span>
//                 </div>
//                 {hoverInfo.description && (
//                   <p className="text-sm text-gray-700 line-clamp-3">{hoverInfo.description}</p>
//                 )}
//               </div>
//             </Popup>
//           )}

//           {zoom <= 7 &&
//             searchedProjects.map(({ center, sector, projectData }, index) =>
//               center ? (
//                 <Marker key={index} longitude={center[0]} latitude={center[1]} anchor="bottom">
//                   <div 
//                     className={`relative group cursor-pointer transition-all duration-300 hover:scale-110 ${
//                       pulseMarkers && selectedSector === sector ? 'animate-pulse' : ''
//                     }`}
//                     onClick={() => handleMarkerClick(center, projectData)}
//                   >
//                     <div className="relative">
//                       <FaMapMarkerAlt
//                         size={12}
//                         className={`transition-all duration-300 ${
//                           sector === "Highways" ? "text-blue-500" :
//                           sector === "Commercial" ? "text-red-500" :
//                           sector === "Infrastructure" ? "text-yellow-500" :
//                           sector === "Construction" ? "text-pink-500" :
//                           sector === "Factory" ? "text-black" :
//                           "text-green-500"
//                         }`}
//                       />
//                       <div className="absolute inset-0 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300"
//                         style={{
//                           backgroundColor: 
//                             sector === "Highways" ? "#3b82f6" :
//                             sector === "Commercial" ? "#ef4444" :
//                             sector === "Infrastructure" ? "#eab308" :
//                             sector === "Construction" ? "#ec4899" :
//                             sector === "Factory" ? "#000000" :
//                             "#10b981"
//                         }}
//                       ></div>
//                     </div>
//                     <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full animate-ping opacity-75"
//                       style={{
//                         backgroundColor: 
//                           sector === "Highways" ? "#3b82f6" :
//                           sector === "Commercial" ? "#ef4444" :
//                           sector === "Infrastructure" ? "#eab308" :
//                           sector === "Construction" ? "#ec4899" :
//                           sector === "Factory" ? "#000000" :
//                           "#10b981"
//                       }}
//                     ></div>
//                     <div 
//                       className={`absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 text-xs font-medium rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
//                         sector === "Highways" ? "bg-blue-500 text-white" :
//                         sector === "Commercial" ? "bg-red-500 text-white" :
//                         sector === "Infrastructure" ? "bg-yellow-500 text-gray-900" :
//                         sector === "Construction" ? "bg-pink-500 text-white" :
//                         sector === "Factory" ? "bg-black text-white" :
//                         "bg-green-500 text-white"
//                       }`}
//                     >
//                       {projectData.project_name}
//                     </div>
//                   </div>
//                 </Marker>
//               ) : null
//             )}
//         </Map>
//       </div>

//       {/* Map Controls */}
//       <div className="absolute z-10 top-32 right-2 flex flex-col space-y-3">
//         <button
//           onClick={resetView}
//           className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group"
//           title="Reset View"
//         >
//           <FiCompass className="text-gray-700 group-hover:text-blue-600 text-lg" />
//         </button>
//         <button
//           onClick={flyToLocation}
//           className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group"
//           title="Find My Location"
//         >
//           <BiCurrentLocation className="text-gray-700 group-hover:text-blue-600 text-lg" />
//         </button>
//       </div>

//       {/* Sidebar Toggle Button */}
//       <button
//         onClick={toggleSidebar}
//         className={`absolute z-10 top-4 left-4 p-3 rounded-full bg-white shadow-lg hover:bg-gray-100 transition-all duration-300 hover:scale-110 active:scale-95 ${
//           sidebarOpen ? 'transform rotate-180' : ''
//         }`}
//         aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
//       >
//         {sidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}
//       </button>

//       {/* Enhanced Sidebar */}
//       <div
//         className={`absolute z-10 top-0 left-0 h-full bg-white shadow-xl transition-all duration-300 ease-in-out overflow-hidden ${
//           sidebarOpen ? "w-80 md:w-96" : "w-0"
//         }`}
//       >
//         {sidebarOpen && (
//           <div className="h-full flex flex-col">
//             {/* Sidebar Header with Gradient */}
//             <div className={`p-4 bg-gradient-to-r ${selectedSector ? sectorGradients[selectedSector] : "from-blue-600 to-blue-800"} text-white flex justify-between items-center`}>
//               <div className="flex items-center">
//                 <RiEarthFill className="text-xl mr-2" />
//                 <h2 className="text-xl font-bold">Global Projects Map</h2>
//               </div>
//               <button 
//                 onClick={toggleSidebar} 
//                 className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
//               >
//                 <FaTimes />
//               </button>
//             </div>

//             {/* Search & Tabs */}
//             <div className="p-4 border-b">
//               <div className="relative mb-4">
//                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                   <FaSearch className="text-gray-400" />
//                 </div>
//                 <input
//                   type="text"
//                   placeholder="Search projects..."
//                   className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                 />
//               </div>

//               <div className="flex border-b">
//                 <button
//                   className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                     activeTab === 'projects' 
//                       ? 'text-blue-600'
//                       : 'text-gray-500 hover:text-gray-700'
//                   }`}
//                   onClick={() => setActiveTab('projects')}
//                 >
//                   Projects
//                   {activeTab === 'projects' && (
//                     <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//                   )}
//                 </button>
//                 <button
//                   className={`flex-1 py-2 px-4 text-center font-medium text-sm focus:outline-none transition-colors duration-300 relative ${
//                     activeTab === 'filters' 
//                       ? 'text-blue-600'
//                       : 'text-gray-500 hover:text-gray-700'
//                   }`}
//                   onClick={() => setActiveTab('filters')}
//                 >
//                   Filters
//                   {activeTab === 'filters' && (
//                     <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></span>
//                   )}
//                 </button>
//               </div>
//             </div>

//             {/* Filters Tab */}
//             {activeTab === 'filters' && (
//               <div className="p-4 border-b">
//                 <div className="flex items-center mb-3">
//                   <FaFilter className="mr-2 text-gray-600" />
//                   <h3 className="font-semibold text-gray-700">Filter by Sector</h3>
//                 </div>
//                 <div className="grid grid-cols-2 gap-2">
//                   {sectors.map(sector => (
//                     <button
//                       key={sector}
//                       onClick={() => toggleFilter(sector)}
//                       className={`px-3 py-2 text-sm rounded-md border flex items-center justify-between transition-all duration-200 hover:shadow-sm active:scale-95 ${
//                         activeFilters.includes(sector)
//                           ? `${sectorColors[sector]} text-white border-transparent shadow-inner`
//                           : `border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300`
//                       }`}
//                     >
//                       <span>{sector}</span>
//                       <span className={`w-3 h-3 rounded-full ${activeFilters.includes(sector) ? 'bg-white bg-opacity-80' : sectorColors[sector]}`}></span>
//                     </button>
//                   ))}
//                 </div>
//                 {activeFilters.length > 0 && (
//                   <button
//                     onClick={() => {
//                       setActiveFilters([]);
//                       setSelectedSector(null);
//                     }}
//                     className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center transition-colors duration-200"
//                   >
//                     <FaTimes className="mr-1" /> Clear all filters
//                   </button>
//                 )}
//               </div>
//             )}

//             {/* Project List */}
//             <div className="flex-1 overflow-y-auto" ref={sidebarRef}>
//               <div className="p-4">
//                 {activeTab === 'projects' && (
//                   <>
//                     <div className="flex items-center justify-between mb-3">
//                       <h3 className="font-semibold text-gray-700 flex items-center">
//                         <IoMdInformationCircleOutline className="mr-2" />
//                         {searchQuery 
//                           ? `Search Results (${searchedProjects.length})`
//                           : activeFilters.length > 0
//                             ? `Filtered Projects (${searchedProjects.length})`
//                             : `All Projects (${searchedProjects.length})`}
//                       </h3>
//                       {(searchQuery || activeFilters.length > 0) && (
//                         <button
//                           onClick={() => {
//                             setSearchQuery("");
//                             setActiveFilters([]);
//                             setSelectedSector(null);
//                           }}
//                           className="text-xs text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center"
//                         >
//                           <FaTimes className="mr-1" /> Clear
//                         </button>
//                       )}
//                     </div>
//                     {searchedProjects.length === 0 ? (
//                       <div className="text-center py-8 transition-opacity duration-300">
//                         <div className="text-gray-400 mb-2">
//                           <FaSearch className="mx-auto text-3xl" />
//                         </div>
//                         <p className="text-gray-500">
//                           {searchQuery 
//                             ? "No projects match your search"
//                             : activeFilters.length > 0
//                               ? "No projects match the selected filters"
//                               : "No projects available"}
//                         </p>
//                       </div>
//                     ) : (
//                       <ul className="space-y-3">
//                         {searchedProjects.map(({ projectData, center, sector }, index) => (
//                           <li
//                             key={index}
//                             onClick={() => handleMarkerClick(center, projectData)}
//                             className={`p-4 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md border hover:border-blue-200 active:scale-98 ${
//                               selectedProject?._id === projectData._id
//                                 ? 'border-blue-300 bg-blue-50'
//                                 : 'border-gray-200 hover:border-gray-300 bg-white'
//                             }`}
//                           >
//                             <div className="flex items-start">
//                               <div className={`w-4 h-4 rounded-full mt-1 mr-3 flex-shrink-0 ${
//                                 sector === "Highways" ? "bg-blue-500" :
//                                 sector === "Commercial" ? "bg-red-500" :
//                                 sector === "Infrastructure" ? "bg-yellow-500" :
//                                 sector === "Construction" ? "bg-pink-500" :
//                                 sector === "Factory" ? "bg-black" :
//                                 "bg-green-500"
//                               }`}></div>
//                               <div className="flex-1 min-w-0">
//                                 <h4 className="font-medium text-gray-800 truncate">{projectData.project_name}</h4>
//                                 <div className="flex items-center mt-1">
//                                   <span className={`text-xs px-2 py-1 rounded-full ${sectorLightColors[sector]} mr-2`}>
//                                     {sector}
//                                   </span>
//                                   {projectData.description && (
//                                     <p className="text-xs text-gray-500 truncate">
//                                       {projectData.description}
//                                     </p>
//                                   )}
//                                 </div>
//                               </div>
//                               <div className="ml-2 text-xs text-gray-400 self-center">
//                                 {new Date(projectData.updatedAt).toLocaleDateString()}
//                               </div>
//                             </div>
//                           </li>
//                         ))}
//                       </ul>
//                     )}
//                   </>
//                 )}
//               </div>
//             </div>

//             {/* Status Bar */}
//             <div className="p-3 bg-gray-50 text-xs text-gray-600 border-t flex justify-between items-center">
//               <div className="flex items-center">
//                 {isAnimating ? (
//                   <span className="flex items-center">
//                     <span className="w-2 h-2 rounded-full bg-blue-500 mr-1 animate-pulse"></span>
//                     Animating...
//                   </span>
//                 ) : (
//                   <span className="flex items-center">
//                     <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
//                     {searchedProjects.length} projects
//                   </span>
//                 )}
//               </div>
//               <div className="flex items-center space-x-2">
//                 <span>Zoom: {zoom.toFixed(2)}</span>
//                 <span className="w-px h-4 bg-gray-300"></span>
//                 <button 
//                   onClick={toggleFullscreen}
//                   className="text-gray-500 hover:text-gray-700 transition-colors"
//                   title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
//                 >
//                   {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Selected Project Panel */}
//       {selectedProject && (
//         <div 
//           className={`absolute z-10 bottom-4 right-4 w-80 bg-white rounded-xl shadow-2xl overflow-hidden transition-all duration-300 transform ${
//             selectedProject ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
//           } ${sidebarOpen ? 'right-4' : 'right-4'}`}
//         >
//           {/* Header */}
//           <div className={`p-4 bg-gradient-to-r ${
//             selectedProject.sector === "Highways" ? "from-blue-500 to-blue-700" :
//             selectedProject.sector === "Commercial" ? "from-red-500 to-red-700" :
//             selectedProject.sector === "Infrastructure" ? "from-yellow-500 to-yellow-700" :
//             selectedProject.sector === "Construction" ? "from-pink-500 to-pink-700" :
//             selectedProject.sector === "Factory" ? "from-gray-800 to-black" :
//             "from-green-500 to-green-700"
//           } text-white flex justify-between items-center rounded-t-xl`}>
//             <h3 className="font-semibold text-lg tracking-wide">Project Details</h3>
//             <button 
//               onClick={() => setSelectedProject(null)} 
//               className="text-white hover:text-blue-200 transition duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20"
//               aria-label="Close Details"
//             >
//               <FaTimes />
//             </button>
//           </div>
        
//           {/* Content */}
//           <div className="p-4 space-y-4">
//             {/* Project Name */}
//             <h4 className="text-lg font-semibold text-gray-800 leading-snug">{selectedProject.project_name}</h4>
        
//             {/* Sector */}
//             <div className="flex items-center text-sm text-gray-600">
//               <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
//                 selectedProject.sector === "Highways" ? "bg-blue-500" :
//                 selectedProject.sector === "Commercial" ? "bg-red-500" :
//                 selectedProject.sector === "Infrastructure" ? "bg-yellow-500" :
//                 selectedProject.sector === "Construction" ? "bg-pink-500" :
//                 selectedProject.sector === "Factory" ? "bg-black" :
//                 "bg-green-500"
//               }`}></span>
//               <span>{selectedProject.sector || "Other"}</span>
//             </div>
        
//             {/* Description */}
//             {selectedProject.description && (
//               <div>
//                 <p className="text-sm text-gray-700 leading-relaxed">{selectedProject.description}</p>
//               </div>
//             )}
        
//             {/* Footer Info */}
//             <div className="text-xs text-gray-500 flex justify-between items-center border-t pt-3">
//               <span>Last updated: {new Date(selectedProject.updatedAt).toLocaleDateString()}</span>
//               <span className="font-mono text-gray-400">ID: {selectedProject._id.slice(-4)}</span>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Mapbox;



// +++++++++++++code here above code is in testing +++++++++++++
// +++++++++++++code here above code is in testing +++++++++++++'💀💀💀💀💀💀💀💀💀💀💀💀💀💀
// +++++++++++++code here above code is in testing +++++++++++++
// +++++++++++++code here above code is in testing +++++++++++++

// import React, { useState, useEffect, useRef } from "react";
// import Map, { NavigationControl, FullscreenControl, Source, Layer, Popup } from "react-map-gl";

// import axios from "axios";
// import { DOMParser } from "xmldom";
// import MapboxGL, { Marker } from "react-map-gl";
// import { kml } from "@tmcw/togeojson";
// import "mapbox-gl/dist/mapbox-gl.css";
// import { useDispatch, useSelector } from "react-redux";
// import { getAllprojects } from "../../FeatureRedux/project/getAllprojects";
// import JSZip from "jszip";

// import { FaMapMarkerAlt } from "react-icons/fa";
// const Mapbox = () => {
//   const [viewState, setViewState] = useState({  
//     latitude: 20.5937,
//     longitude: 78.9629,
//     zoom: 0.5,
//     pitch: 0,
//     bearing: 180,
//   });
//   const [hoverInfo, setHoverInfo] = useState(null);


//   // const { data  , loading, isError , isSuccess } = useSelector((s) => s.getAllprojects);
//   const dispatch = useDispatch();
//   const [geoJsonData, setGeoJsonData] = useState([]);
//   const mapRef = useRef(null);
//   const animationRef = useRef(null);
//   const [zoom, setZoom] = useState(viewState.zoom);


//   const [data, setData] = useState([])

//   useEffect(() => {
//     (async () => {
//       try {
//         const response = await dispatch(getAllprojects()).unwrap();
//         setData(response.projects)
//         // console.log("Response Data:::::::::::::::::", response.projects)
//         // console.log("This is the data for kml", data)
//         // Handle the response directly here
//       } catch (error) {
//         // console.error("Error fetching projects:", error);
//       }
//     })();
//   }, [dispatch]);



//   useEffect(() => {
//     // console.log(`this is the data of projects ${JSON.stringify(data, null, " ")} hello`);
//   }, [data])

//   const fetchAndConvertKML = async (url) => {
//     try {
//       const isKMZ = url.endsWith(".kmz");

//       if (isKMZ) {
//         // Fetch KMZ as a binary blob
//         const response = await axios.get(url, { responseType: "arraybuffer" });
//         const zip = await JSZip.loadAsync(response.data);

//         // Most KMZs have 'doc.kml' or sometimes a custom name
//         const kmlFile = Object.values(zip.files).find((file) => file.name.endsWith(".kml"));
//         if (!kmlFile) {
//           // console.warn("No .kml file found inside KMZ:", url);
//           return null;
//         }

//         const kmlText = await kmlFile.async("text");
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(kmlText, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       } else {
//         // Handle regular KML
//         const response = await axios.get(url, { responseType: "text" });
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(response.data, "text/xml");
//         const geoJson = kml(kmlData);
//         return geoJson;
//       }
//     } catch (error) {
//       // console.error("Error fetching or converting KML/KMZ:", error, "URL:", url);
//       return null;
//     }
//   };



//   useEffect(() => {
//     const fetchAllKMLs = async () => {
//       if (!Array.isArray(data) || data.length === 0) {
//         // console.warn("No project data available.");
//         return;
//       }

//       let kmlFiles = [];

//       for (const project of data) {
//         if (Array.isArray(project.kml) && project.kml.length > 0) {
//           for (const file of project.kml) {
//             try {
//               const geoJson = await fetchAndConvertKML(file.url);

//               // ✅ Add project_name to each feature
//               if (geoJson && geoJson.features) {
//                 geoJson.features.forEach((feature) => {
//                   if (!feature.properties) feature.properties = {};
//                   feature.properties.project_name = project.project_name;
//                 });
//               }

//               if (geoJson && geoJson.features && geoJson.features.length > 0) {
//                 const allCoords = geoJson.features
//                   .map((f) => {
//                     if (f.geometry.type === "Point") {
//                       return [f.geometry.coordinates];
//                     } else if (
//                       f.geometry.type === "LineString" ||
//                       f.geometry.type === "MultiPoint"
//                     ) {
//                       return f.geometry.coordinates;
//                     } else if (
//                       f.geometry.type === "Polygon" ||
//                       f.geometry.type === "MultiLineString"
//                     ) {
//                       return f.geometry.coordinates.flat();
//                     } else if (f.geometry.type === "MultiPolygon") {
//                       return f.geometry.coordinates.flat(2);
//                     }
//                     return [];
//                   })
//                   .flat();

//                 if (allCoords.length > 0) {
//                   const lngs = allCoords.map((c) => c[0]);
//                   const lats = allCoords.map((c) => c[1]);

//                   const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
//                   const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;

//                   const center = [avgLng, avgLat];

//                   kmlFiles.push({
//                     geoJson,
//                     projectId: project._id,
//                     center,
//                     sector: project.sector || "",
//                   });
//                 } else {
//                   // console.warn("No coordinates found for:", file.url);
//                 }
//               } else {
//                 // console.warn("Failed to convert or empty GeoJSON for:", file.url);
//               }
//             } catch (err) {
//               // console.error("Error processing file:", file.url, err);
//             }
//           }
//         }
//       }

//       setGeoJsonData(kmlFiles);
//     };

//     fetchAllKMLs();
//   }, [data]);


//   useEffect(() => {
//     const animateGlobe = () => {
//       const startTime = Date.now();
//       const duration = 4000;
//       const startZoom = 0.5;
//       const endZoom = 3;
//       const startBearing = 180;
//       const endBearing = 0;

//       const animateFrame = () => {
//         const now = Date.now();
//         const progress = Math.min((now - startTime) / duration, 1);
//         const easeProgress = progress < 0.5 ? 4 * progress ** 3 : 1 - ((-2 * progress + 2) ** 3) / 2;

//         setViewState((prev) => ({
//           ...prev,
//           zoom: startZoom + (endZoom - startZoom) * easeProgress,
//           bearing: startBearing + (endBearing - startBearing) * easeProgress,
//         }));

//         if (progress < 1) {
//           animationRef.current = requestAnimationFrame(animateFrame);
//         }
//       };

//       animationRef.current = requestAnimationFrame(animateFrame);
//     };

//     const timer = setTimeout(animateGlobe, 1000);
//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) cancelAnimationFrame(animationRef.current);
//     };
//   }, []);

//   return (
//     <div style={{ height: "100%", width: "100%", position: "relative" }}>
//       <Map
//         {...viewState}
//         ref={mapRef}
//         mapboxAccessToken="pk.eyJ1IjoibmlraXRhY2hhdWhhbjEyMyIsImEiOiJjbGwwaWxrdzEwZW02M2pxcjN4eHo1bDR1In0.I4yZh8CAQOz2c63IsCBOpg"
//         style={{ width: "100%", height: "100%" }}
//         projection="globe"
//         mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
//         onMove={(evt) => {
//           setViewState(evt.viewState);
//           setZoom(evt.viewState.zoom);
//         }}
//         onMouseMove={(event) => {
//           const features = event.features;
//           const hoveredFeature = features && features[0];

//           if (hoveredFeature && hoveredFeature.properties?.project_name) {
//             setHoverInfo({
//               longitude: event.lngLat.lng,
//               latitude: event.lngLat.lat,
//               name: hoveredFeature.properties.project_name,
//             });
//           } else {
//             setHoverInfo(null);
//           }
//         }}
//         onMouseLeave={() => setHoverInfo(null)}
//         interactiveLayerIds={geoJsonData.map((_, i) => `kml-layer-${i}`)}

//       >
//         <NavigationControl position="top-right" />
//         <FullscreenControl position="top-right" />

//         {geoJsonData.map(({ geoJson, projectId }, index) => (
//           <Source key={`${projectId}-${index}`} id={`kml-data-${index}`} type="geojson" data={geoJson}>
//             <Layer
//               id={`kml-layer-${index}`}
//               type="line"
//               paint={{
//                 "line-color": "#ff0000",
//                 "line-width": 2,
//               }}
//             />
//           </Source>
//         ))}
//         {hoverInfo && (
//           <Popup
//             longitude={hoverInfo.longitude}
//             latitude={hoverInfo.latitude}
//             closeButton={false}
//             closeOnClick={false}
//             anchor="top"
//           >
//             <div style={{ fontSize: 12, fontWeight: "bold", cursor: "pointer" }}>
//               {hoverInfo.name}
//             </div>
//           </Popup>

//         )}

//         {zoom <= 7 &&
//           geoJsonData.map(({ center, sector }, index) =>
//             center ? (
//               <Marker key={index} longitude={center[0]} latitude={center[1]} anchor="bottom">
//                 <FaMapMarkerAlt
//                   size={24}
//                   style={{ cursor: "pointer" }}
//                   onClick={() => {
//                     if (mapRef.current) {
//                       mapRef.current.flyTo({
//                         center: [center[0], center[1]],
//                         zoom: 12,
//                         speed: 1.2, // animation speed
//                         curve: 1.42,
//                         easing: (t) => t,
//                         essential: true,
//                       });
//                     }
//                   }}
//                   color={
//                     sector === "Highways"
//                       ? "blue"
//                       : sector === "Commercial"
//                         ? "red"
//                         : sector === "Infrastructure"
//                           ? "yellow"
//                           : sector === "Construction"
//                             ? "pink"
//                             : sector === "Factory"
//                               ? "black"
//                               : "green"
//                   }
//                 />
//               </Marker>
//             ) : null
//           )}

//       </Map>
//     </div>
//   );
// };

// export default Mapbox;



// import React, { useState, useEffect, useRef } from 'react';
// import Map, { NavigationControl, FullscreenControl, Source, Layer } from 'react-map-gl';
// import axios from 'axios';
// import { DOMParser } from 'xmldom';
// import { kml } from '@tmcw/togeojson';
// import 'mapbox-gl/dist/mapbox-gl.css';
// import { getAllprojects } from '../../FeatureRedux/project/getAllprojects';
// import { useDispatch, useSelector } from 'react-redux';

// const Mapbox = () => {
//   const [viewState, setViewState] = useState({
//     latitude: 20.5937, // Centered over India
//     longitude: 78.9629,
//     zoom: 0.5, // Start fully zoomed out
//     pitch: 0,
//     bearing: 180, // Start rotated 180 degrees (showing the "back" of the globe)
//   });

//   const {data , loading , isError} = useSelector((s)=>s.getAllprojects);


//   const dispatch = useDispatch()

//   useEffect(()=>{
//     dispatch(getAllprojects())
//   },[])

//   const [geoJsonData, setGeoJsonData] = useState(null);
//   const mapRef = useRef(null);
//   const animationRef = useRef(null);

//   const KML_URL = "https://res.cloudinary.com/dziyqo8zo/raw/upload/v1739772281/Sheohar_AOI_fmlqcd.kml";  

//   useEffect(() => {
//     const fetchAndConvertKML = async () => {
//       try {
//         const response = await axios.get(KML_URL);
//         const kmlText = response.data;

//         // Convert KML to GeoJSON
//         const parser = new DOMParser();
//         const kmlData = parser.parseFromString(kmlText, 'text/xml');
//         const geoJson = kml(kmlData);

//         setGeoJsonData(geoJson);
//       } catch (error) {
//         console.error("Error fetching or converting KML:", error);
//       }
//     };

//     fetchAndConvertKML();
//   }, []);

//   useEffect(() => {
//     // Animation for rotating and zooming in
//     const animateGlobe = () => {
//       const startTime = Date.now();
//       const duration = 4000; // 4 seconds for smoother animation
//       const startZoom = 0.5;
//       const endZoom = 3;
//       const startBearing = 180;
//       const endBearing = 0;

//       const animateFrame = () => {
//         const now = Date.now();
//         const progress = Math.min((now - startTime) / duration, 1);

//         // Cubic easing function for extra smoothness
//         const easeProgress = progress < 0.5 
//           ? 4 * progress * progress * progress 
//           : 1 - Math.pow(-2 * progress + 2, 3) / 2;

//         const newZoom = startZoom + (endZoom - startZoom) * easeProgress;
//         const newBearing = startBearing + (endBearing - startBearing) * easeProgress;

//         setViewState(prev => ({
//           ...prev,
//           zoom: newZoom,
//           bearing: newBearing,
//         }));

//         if (progress < 1) {
//           animationRef.current = requestAnimationFrame(animateFrame);
//         }
//       };

//       animationRef.current = requestAnimationFrame(animateFrame);
//     };

//     // Start animation after a small delay to ensure map is loaded
//     const timer = setTimeout(animateGlobe, 1000);

//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) {
//         cancelAnimationFrame(animationRef.current);
//       }
//     };
//   }, []);

//   return (
//     <div style={{ height: '100%', width: '100%', position: 'relative' }}>
//       <Map
//         {...viewState}
//         ref={mapRef}
//         mapboxAccessToken={
//           'pk.eyJ1IjoibmlraXRhY2hhdWhhbjEyMyIsImEiOiJjbGwwaWxrdzEwZW02M2pxcjN4eHo1bDR1In0.I4yZh8CAQOz2c63IsCBOpg'}
//         style={{ width: '100%', height: '100%' }}

//         // ✅ Use Globe Projection
//         projection="globe"

//         // ✅ Satellite with Labels (City names, roads, etc.)
//         mapStyle="mapbox://styles/mapbox/satellite-streets-v12"

//         onMove={(evt) => setViewState(evt.viewState)}
//       >
//         <NavigationControl position="top-right" />
//         <FullscreenControl position="top-right" />

//         {/* Display the converted KML as a GeoJSON source */}
//         {geoJsonData && (
//           <Source id="kml-data" type="geojson" data={geoJsonData}>
//             <Layer
//               id="kml-layer"
//               type="line"
//               paint={{
//                 'line-color': '#ff0000',
//                 'line-width': 2,
//               }}
//             />
//           </Source>
//         )}
//       </Map>
//     </div>
//   );
// };

// export default Mapbox;















